/**
 * EdgeDocs Input Handler
 *
 * Basic keyboard input handling for system font editor (Phase 1A)
 */

import React from 'react';
import {
  DocumentState,
  SelectionRange,
  InputCommand,
  ApplyResult,
  HistoryState,
  ClipboardPayload,
  applyInsert,
  applyDeleteRange,
  normalizeSelection
} from './EdgeDocsTypes';
import type { Line, CaretPosition } from '../../core/types/Typesetter';
import { InputLogger } from '../../../../utils/loggers/InputLogger';

// ✅ MANDATORY: Use ONLY WASM Line type - removed legacy WrappedLine
// ✅ MANDATORY: All caret positioning from caret_map - no Canvas math

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false; // 🚀 DISABLED for production performance
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

type CaretGeom = { x: number; y: number; height: number };

export interface InputEvent {
  type: 'insert' | 'delete' | 'enter' | 'navigation' | 'pageBreak' | 'softLineBreak';
  position: number;
  text?: string;
  length?: number;
}

// ============================================================================
// OPTION C: Edit command type for WASM as Single Source of Truth
// ============================================================================
export interface EditCommand {
  type: 'insert' | 'delete' | 'replace';
  position: number;      // Grapheme index
  text?: string;         // For insert/replace
  deleteCount?: number;  // For delete/replace (grapheme count)
}

export interface EditCommandResult {
  success: boolean;
  newText: string;
  newCursorPosition: number;  // Grapheme index
  error?: string;
}

export interface InputHandlerConfig {
  onTextChange: (newText: string, selection: SelectionRange) => void;
  onSelectionChange?: (selection: SelectionRange) => void;
  onHistoryChange?: (canUndo: boolean, canRedo: boolean) => void;
  maxLength?: number;

  // ✅ MANDATORY: WASM-only data sources (no Canvas fallbacks)
  getWasmLines: () => Line[];                        // WASM snapshot.lines (REQUIRED)
  getCaretMap: () => CaretPosition[];                 // WASM snapshot.caret_map (REQUIRED)
  getGraphemeToUtf16: () => number[];                 // Grapheme→UTF-16 mapping (REQUIRED)
  
  // 🆕 FALLBACK: Get ALL lines from typesetter client cache (for cross-page navigation)
  // Used when getWasmLines returns lines that don't contain the cursor position
  getAllCachedLines?: () => Line[];
  getAllCachedCaretPositions?: () => CaretPosition[];

  // overlay updates
  onCursorChange?: (cursorIndex: number, caret: CaretGeom) => void;
  onSelectionHighlightChange?: (ranges: Array<{start: number, end: number}>) => void;
  
  // ============================================================================
  // 🆕 OPTION C: WASM as Single Source of Truth
  // When enabled, text edits are sent to WASM first, and the response is used
  // to update local state. This eliminates optimistic updates and ensures
  // WASM is always the source of truth.
  // ============================================================================
  useOptionC?: boolean;  // Enable Option C mode (default: false)
  onEditCommand?: (command: EditCommand) => Promise<EditCommandResult>;  // Send edit to WASM
  
  // 🆕 Undo/Redo callbacks (for Option C mode using SharedInputContext's UndoManager)
  onUndo?: () => Promise<void>;
  onRedo?: () => Promise<void>;
  
  // 🆕 Phase F (2026-01-21): Character-level formatting callbacks (Ctrl+B, Ctrl+I)
  onToggleBold?: () => Promise<void>;
  onToggleItalic?: () => Promise<void>;
}

export class EdgeDocsInputHandler {
  private config: InputHandlerConfig;
  private currentState: DocumentState;
  private history: HistoryState;
  private eventHistory: InputEvent[] = [];

  // Selection drag state
  private isDragging: boolean = false;
  private dragAnchor: number = 0;

  // Arrow key navigation
  private desiredVisualX: number | null = null;

  // Typing coalescing
  private typingGroupId: string | null = null;
  private lastEditTime: number = 0;

  // State sync guard: prevents accepting input while React update is pending
  private pendingStateUpdate: boolean = false;
  
  // ✅ V9: IME composition state
  private isComposing: boolean = false;
  private compositionStart: number = 0; // Grapheme index where composition started
  private compositionText: string = ''; // Current composition buffer
  
  // ✅ OPTION C: Held-key debouncing to prevent margin overflow during rapid input
  // When the same key is held down (repeat), limit rate to ~100ms between keystrokes
  private lastHeldKey: string = '';
  private lastHeldKeyTime: number = 0;
  private readonly HELD_KEY_MIN_INTERVAL_MS = 100; // Max ~10 chars/second when holding
  
  // ✅ OPTION C: Operation queue to prevent concurrent WASM operations
  // This ensures edits are processed in order and prevents race conditions
  private optionCOperationQueue: Promise<void> = Promise.resolve();
  private optionCPendingOperations: number = 0;

  constructor(config: InputHandlerConfig, initialState: DocumentState, historyConfig = { maxDepth: 200 }) {
    this.config = config;
    // Ensure initial state has selection
    this.currentState = {
      text: initialState.text,
      selection: initialState.selection || { anchor: initialState.cursorPosition || 0, focus: initialState.cursorPosition || 0 },
      cursorPosition: initialState.cursorPosition
    };
    this.history = {
      undo: [],
      redo: [],
      maxDepth: historyConfig.maxDepth
    };
  }

  /**
   * Update undo/redo callbacks after initialization
   * This is needed because useCallback refs may not be stable at mount time
   */
  setUndoRedoCallbacks(onUndo?: () => Promise<void>, onRedo?: () => Promise<void>): void {
    this.config.onUndo = onUndo;
    this.config.onRedo = onRedo;
  }

  /**
   * 🆕 Phase F (2026-01-21): Update formatting callbacks after initialization
   * This enables Ctrl+B and Ctrl+I keyboard shortcuts
   */
  setFormattingCallbacks(onToggleBold?: () => Promise<void>, onToggleItalic?: () => Promise<void>): void {
    this.config.onToggleBold = onToggleBold;
    this.config.onToggleItalic = onToggleItalic;
  }

  /**
   * 🔧 FIX (2026-01-24): Set selection change callback after initialization
   * This enables toolbar operations to track the last non-collapsed selection
   * for applying formatting to selected text (e.g., font size changes)
   */
  setOnSelectionChange(callback?: (selection: SelectionRange) => void): void {
    this.config.onSelectionChange = callback;
  }

  /**
   * Main command dispatch method
   */
  async dispatch(cmd: InputCommand): Promise<boolean> {
    const result = await this.applyCommand(cmd);
    if (result.op) {
      this.pushToHistory(result.op);
    }
    this.updateState(result.state);
    return true;
  }

  /**
   * Apply a command to current state
   */
  private async applyCommand(cmd: InputCommand): Promise<ApplyResult> {
    switch (cmd.kind) {
      case 'set-selection':
        return { state: { ...this.currentState, selection: cmd.selection } };

      case 'insert-text':
        return applyInsert(this.currentState, cmd.text, performance.now(), this.typingGroupId || undefined);

      case 'delete-backward':
      case 'delete-forward': {
        const { start, end } = normalizeSelection(this.currentState.selection);
        const isBackward = cmd.kind === 'delete-backward';

        if (start === end) {
          // Delete single char/word/line
          const pos = isBackward ? start : end;
          const newPos = this.adjustPositionForDelete(pos, cmd.unit, isBackward);
          if (newPos !== pos) {
            return applyDeleteRange(this.currentState, Math.min(pos, newPos), Math.max(pos, newPos));
          }
        } else {
          // Delete selection
          return applyDeleteRange(this.currentState, start, end);
        }
        return { state: this.currentState };
      }

      case 'copy':
        this.handleCopy();
        return { state: this.currentState };

      case 'cut': {
        const result = await this.handleCut();
        return result;
      }

      case 'paste':
        return applyInsert(this.currentState, cmd.payload.text);

      case 'undo':
        return this.handleUndo();

      case 'redo':
        return this.handleRedo();

      default:
        return { state: this.currentState };
    }
  }

  /**
   * Helper methods for command execution
   */
  private adjustPositionForDelete(pos: number, unit: 'char' | 'word' | 'line', backward: boolean): number {
    const text = this.currentState.text;
    switch (unit) {
      case 'char':
        return backward ? Math.max(0, pos - 1) : Math.min(text.length, pos + 1);
      case 'word':
        // Basic word boundary detection (can be enhanced)
        if (backward) {
          let i = pos - 1;
          while (i > 0 && /\s/.test(text[i])) i--;
          while (i > 0 && /\S/.test(text[i - 1])) i--;
          return i;
        } else {
          let i = pos;
          while (i < text.length && /\s/.test(text[i])) i++;
          while (i < text.length && /\S/.test(text[i])) i++;
          return i;
        }
      case 'line':
        // For now, treat as whole document (can be enhanced with layout info)
        return backward ? 0 : text.length;
      default:
        return pos;
    }
  }

  private async handleCopy(): Promise<void> {
    const { start, end } = normalizeSelection(this.currentState.selection);
    if (start !== end) {
      const selectedText = this.currentState.text.slice(start, end);
      InputLogger.copy(selectedText.length, { start, end });
      try {
        await navigator.clipboard.writeText(selectedText);
      } catch (e) {
        InputLogger.editError('clipboard-copy-failed', e as Error);
      }
    }
  }

  private async handleCut(): Promise<ApplyResult> {
    const { start, end } = normalizeSelection(this.currentState.selection);
    if (start !== end) {
      const selectedText = this.currentState.text.slice(start, end);
      InputLogger.cut(selectedText.length, { start, end });
      try {
        await navigator.clipboard.writeText(selectedText);
        return applyDeleteRange(this.currentState, start, end);
      } catch (e) {
        InputLogger.editError('clipboard-cut-failed', e as Error);
      }
    }
    return { state: this.currentState };
  }

  private handleUndo(): ApplyResult {
    if (this.history.undo.length === 0) return { state: this.currentState };

    const op = this.history.undo.pop()!;
    this.history.redo.push(op);

    // Invert the operation
    let newState = this.currentState;
    if (op.type === 'insert' && op.insertedText) {
      // Undo insert = delete the inserted text
      const deleteStart = op.at;
      const deleteEnd = op.at + op.insertedText.length;
      const result = applyDeleteRange(this.currentState, deleteStart, deleteEnd);
      newState = result.state;
    } else if (op.type === 'delete' && op.deletedText) {
      // Undo delete = insert the deleted text
      const result = applyInsert(this.currentState, op.deletedText);
      newState = result.state;
    } else if (op.type === 'replace' && op.deletedText && op.insertedText) {
      // Undo replace = replace inserted with deleted
      const result = applyInsert(this.currentState, op.deletedText || '');
      newState = result.state;
    }

    this.updateHistoryState();
    return { state: newState };
  }

  private handleRedo(): ApplyResult {
    if (this.history.redo.length === 0) return { state: this.currentState };

    const op = this.history.redo.pop()!;
    this.history.undo.push(op);

    // Re-apply the operation
    let newState = this.currentState;
    if (op.type === 'insert' && op.insertedText) {
      const result = applyInsert(this.currentState, op.insertedText);
      newState = result.state;
    } else if (op.type === 'delete' && op.deletedText) {
      const result = applyDeleteRange(this.currentState, op.at, op.at + op.deletedText.length);
      newState = result.state;
    }

    this.updateHistoryState();
    return { state: newState };
  }

  private pushToHistory(op: import('./EdgeDocsTypes').EditOperation): void {
    this.history.undo.push(op);
    this.history.redo = []; // Clear redo stack on new edit

    // Enforce max depth
    if (this.history.undo.length > this.history.maxDepth) {
      this.history.undo.shift();
    }

    this.updateHistoryState();
  }

  private updateHistoryState(): void {
    this.config.onHistoryChange?.(
      this.history.undo.length > 0,
      this.history.redo.length > 0
    );
  }

  /**
   * Selection drag methods
   * Note: Individual drag logs disabled - consolidated click logging in ClickEventCollector
   */
  beginSelectionDrag(anchor: number): void {
    // InputLogger.selection.dragStart(anchor); // Disabled: consolidated in ClickEventCollector
    this.isDragging = true;
    this.dragAnchor = anchor;
    this.setSelection({ anchor, focus: anchor });
  }

  updateSelectionDrag(current: number): void {
    if (this.isDragging) {
      // InputLogger.selection.dragUpdate(current, { anchor: this.dragAnchor }); // Disabled: consolidated
      this.setSelection({ anchor: this.dragAnchor, focus: current });
    }
  }

  endSelectionDrag(): void {
    if (this.isDragging) {
      const { start, end } = normalizeSelection(this.currentState.selection);
      // InputLogger.selection.dragEnd(start, end); // Disabled: consolidated in ClickEventCollector
    }
    this.isDragging = false;
  }

  setSelection(selection: SelectionRange): void {
    DEBUG_ENABLED && console.log('🖱️ [EdgeDocsInput] setSelection:', selection);
    // InputLogger.selection.set(selection.anchor, selection.focus); // Disabled: consolidated
    this.currentState = { ...this.currentState, selection };
    this.config.onSelectionChange?.(selection);
    this.updateSelectionHighlights();
    this.emitCursorChange(selection.focus); // keep caret in sync
  }

  private updateSelectionHighlights(): void {
    const { start, end } = normalizeSelection(this.currentState.selection);
    DEBUG_ENABLED && console.log('🖱️ [EdgeDocsInput] updateSelectionHighlights:', { start, end, hasCallback: !!this.config.onSelectionHighlightChange });
    if (start !== end) {
      this.config.onSelectionHighlightChange?.([{ start, end }]);
    } else {
      this.config.onSelectionHighlightChange?.([]);
    }
  }

  private updateState(newState: DocumentState): void {
    this.currentState = newState;
    // ✅ FIX: Track last sent text to prevent echo-back overwrite
    (this as any).lastSentText = newState.text;
    this.config.onTextChange(newState.text, newState.selection);
    this.updateSelectionHighlights();
  }

  /**
   * Normalize keyboard event to character (robust across browsers)
   */
  private keyToChar(event: KeyboardEvent): string | null {
    // Don't insert text for shortcuts
    if (event.ctrlKey || event.metaKey) return null;

    // Robust space detection across browsers
    if (event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space') {
      return ' ';
    }

    // Printable characters (single code unit, not control chars)
    if (event.key && event.key.length === 1 && !['\n', '\r', '\t'].includes(event.key)) {
      return event.key;
    }

    return null;
  }

  /**
   * Handle keyboard input events
   */
  handleKeyDown(event: KeyboardEvent): boolean {
    const { key, ctrlKey, metaKey, shiftKey } = event;
    
    // Log keydown event
    InputLogger.keydown(key, true, { ctrlKey, metaKey, shiftKey, repeat: event.repeat });
    
    // ✅ OPTION C: Debounce held keys to prevent margin overflow during rapid input
    // Only applies to printable characters (single char keys) being held down
    const now = Date.now();
    const isPrintableChar = key.length === 1 && !ctrlKey && !metaKey;
    const isHeldKey = event.repeat || (isPrintableChar && key === this.lastHeldKey && 
                      (now - this.lastHeldKeyTime) < 200); // Consider "held" if same key within 200ms
    
    if (isPrintableChar && isHeldKey) {
      const timeSinceLastKey = now - this.lastHeldKeyTime;
      if (timeSinceLastKey < this.HELD_KEY_MIN_INTERVAL_MS) {
        // Skip this keystroke - too fast during held key
        event.preventDefault();
        return true;
      }
    }
    
    // Track for held-key detection
    if (isPrintableChar) {
      this.lastHeldKey = key;
      this.lastHeldKeyTime = now;
    } else {
      // Reset held-key tracking on non-printable keys
      this.lastHeldKey = '';
    }
    
    // 🚨 CRITICAL DIAGNOSTIC: Log all keydown events
    DEBUG_ENABLED && console.log('⌨️ [handleKeyDown] Key pressed:', {
      key,
      code: event.code,
      ctrlKey,
      metaKey,
      shiftKey,
      isComposing: this.isComposing,
      currentCursorPosition: this.currentState.selection.focus,
      textLength: this.currentState.text.length
    });
    
    // ✅ V9: During IME composition, ignore most keys (let browser handle composition)
    if (this.isComposing) {
      // Allow navigation keys during composition
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
        // Let browser handle navigation within composition
        return false;
      }
      // Backspace during composition edits the composition buffer (handled by browser)
      if (key === 'Backspace' || key === 'Delete') {
        return false; // Let browser handle
      }
      // Block other keys during composition
      return false;
    }

    // Handle special keys first
    if (this.handleSpecialKey(key, ctrlKey || metaKey, shiftKey)) {
      event.preventDefault();
      return true;
    }

    // Handle text input via normalizer
    const ch = this.keyToChar(event);
    if (ch !== null) {
      this.insertText(ch);
      event.preventDefault(); // prevent page scroll on space, default browser behavior
      return true;
    }

    return false; // Let default handling occur
  }
  
  /**
   * ✅ V9: Handle composition start (IME input begins)
   */
  handleCompositionStart(event: CompositionEvent): void {
    DEBUG_ENABLED && console.log('🎌 [IME] Composition started');
    InputLogger.ime.start({ position: this.currentState.selection.focus });
    this.isComposing = true;
    this.compositionStart = this.currentState.selection.focus;
    this.compositionText = '';
  }
  
  /**
   * ✅ V9: Handle composition update (IME input changes)
   */
  handleCompositionUpdate(event: CompositionEvent): void {
    DEBUG_ENABLED && console.log('🎌 [IME] Composition update:', event.data);
    InputLogger.ime.update(event.data || '', { position: this.compositionStart });
    this.compositionText = event.data || '';
    // Don't mutate main document during composition
  }
  
  /**
   * ✅ V9: Handle composition end (IME input committed)
   */
  handleCompositionEnd(event: CompositionEvent): void {
    DEBUG_ENABLED && console.log('🎌 [IME] Composition ended, committing:', event.data);
    InputLogger.ime.end(event.data || this.compositionText, { position: this.compositionStart });
    this.isComposing = false;
    
    const finalText = event.data || this.compositionText;
    if (finalText) {
      // Commit composition as single insertion at composition start position
      const mapping = this.config.getGraphemeToUtf16?.() ?? [];
      
      if (mapping.length > 0 && this.compositionStart < mapping.length) {
        // Insert at UTF-16 offset corresponding to composition start grapheme
        const utf16Offset = mapping[this.compositionStart];
        const { text } = this.currentState;
        const newText = text.slice(0, utf16Offset) + finalText + text.slice(utf16Offset);
        
        // Move cursor to end of inserted text (approximate grapheme count)
        const newCursorPosition = this.compositionStart + finalText.length;
        this.updateText(newText, newCursorPosition);
        
        DEBUG_ENABLED && console.log('✅ [IME] Committed composition:', {
          startGrapheme: this.compositionStart,
          utf16Offset,
          insertedText: finalText,
          newCursorPosition
        });
      } else {
        // Fallback: insert at current position
        this.insertText(finalText);
      }
    }
    
    this.compositionText = '';
  }

  /**
   * Handle special keys (backspace, delete, enter, navigation)
   */
  private handleSpecialKey(key: string, ctrlOrMeta: boolean, shift: boolean): boolean {
    switch (key) {
      case 'Backspace':
        this.handleBackspace();
        return true;

      case 'Delete':
        this.handleDelete();
        return true;

      case 'Enter':
        if (ctrlOrMeta) {
          // Ctrl+Enter = Page Break (like MS Word)
          this.handlePageBreak();
        } else if (shift) {
          // Shift+Enter = Soft line break (same paragraph, new line)
          this.handleSoftLineBreak();
        } else {
          // Enter = Paragraph break (new paragraph)
          this.handleEnter();
        }
        return true;

      case 'Tab':
        // Insert tab character (or spaces, depending on preference)
        this.insertText('\t'); // Can change to '  ' (2 spaces) or '    ' (4 spaces) if preferred
        return true;

      case 'ArrowLeft':
        // 🔧 FIX (2026-01-20): MS Word behavior - collapse selection to start, don't move further
        if (!shift && this.hasSelection()) {
          const { start } = normalizeSelection(this.currentState.selection);
          this.setCursorPosition(start);
        } else if (ctrlOrMeta) {
          this.moveByWord(-1, shift);
        } else {
          this.moveCursor(-1, shift);
        }
        // horizontal move resets sticky column
        this.desiredVisualX = null;
        return true;

      case 'ArrowRight':
        // 🔧 FIX (2026-01-20): MS Word behavior - collapse selection to end, don't move further
        if (!shift && this.hasSelection()) {
          const { end } = normalizeSelection(this.currentState.selection);
          this.setCursorPosition(end);
        } else if (ctrlOrMeta) {
          this.moveByWord(1, shift);
        } else {
          this.moveCursor(1, shift);
        }
        this.desiredVisualX = null;
        return true;

      case 'ArrowUp':
        this.moveCursorVisualLine(-1, shift);
        return true;

      case 'ArrowDown':
        this.moveCursorVisualLine(1, shift);
        return true;

      case 'Home':
        // ✅ Ctrl+Home: Jump to document start
        if (ctrlOrMeta) {
          if (shift) this.extendSelection(0);
          else this.setCursorPosition(0);
        } else {
          this.moveCursorToVisualLineStart(shift);
        }
        this.desiredVisualX = null;
        return true;

      case 'End':
        // ✅ Ctrl+End: Jump to document end
        if (ctrlOrMeta) {
          const mapping = this.getGraphemeMapping();
          // ✅ FIX: Document end is after last grapheme (N+1 position for N graphemes)
          const docEnd = mapping.length > 0 ? mapping.length : 0;
          if (shift) this.extendSelection(docEnd);
          else this.setCursorPosition(docEnd);
        } else {
          this.moveCursorToVisualLineEnd(shift);
        }
        this.desiredVisualX = null;
        return true;

      case 'PageUp':
        // 🔧 FIX (2026-01-20): Implement PageUp - jump ~20 visual lines up (MS Word behavior)
        // Page navigation uses the same sticky column behavior as Up/Down
        this.moveCursorVisualLine(-this.getPageLineCount(), shift);
        return true;

      case 'PageDown':
        // 🔧 FIX (2026-01-20): Implement PageDown - jump ~20 visual lines down (MS Word behavior)
        this.moveCursorVisualLine(this.getPageLineCount(), shift);
        return true;

      case 'a':
        if (ctrlOrMeta) {
          this.selectAll();
          return true;
        }
        break;

      case 'c':
        if (ctrlOrMeta) {
          // ✅ DON'T prevent default - let browser fire native copy event
          // ClipboardBridge will intercept and handle it
          DEBUG_ENABLED && console.log('📋 [EdgeDocsInput] Ctrl+C detected - letting browser handle (ClipboardBridge)');
          return false;
        }
        break;

      case 'v':
        if (ctrlOrMeta) {
          // ✅ DON'T prevent default - let browser fire native paste event
          // ClipboardBridge will intercept and handle it
          DEBUG_ENABLED && console.log('📋 [EdgeDocsInput] Ctrl+V detected - letting browser handle (ClipboardBridge)');
          return false;
        }
        break;

      case 'x':
        if (ctrlOrMeta) {
          // ✅ DON'T prevent default - let browser fire native cut event
          // ClipboardBridge will intercept and handle it
          DEBUG_ENABLED && console.log('📋 [EdgeDocsInput] Ctrl+X detected - letting browser handle (ClipboardBridge)');
          return false;
        }
        break;

      case 'z':
        if (ctrlOrMeta) {
          this.undo();
          return true;
        }
        break;

      case 'y':
        if (ctrlOrMeta && shift) {
          this.redo();
          return true;
        }
        break;

      // 🆕 Phase F (2026-01-21): Character-level formatting shortcuts
      case 'b':
        if (ctrlOrMeta) {
          // Ctrl+B = Toggle Bold
          if (this.config.onToggleBold) {
            this.config.onToggleBold().catch(err => {
              InputLogger.editError('toggle-bold-failed', err as Error);
            });
          } else {
            InputLogger.commandBlocked('toggle-bold', 'handler not configured');
          }
          return true;
        }
        break;

      case 'i':
        if (ctrlOrMeta) {
          // Ctrl+I = Toggle Italic
          if (this.config.onToggleItalic) {
            this.config.onToggleItalic().catch(err => {
              InputLogger.editError('toggle-italic-failed', err as Error);
            });
          } else {
            InputLogger.commandBlocked('toggle-italic', 'handler not configured');
          }
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Insert text at cursor position
   * 
   * ✅ OPTION C: When useOptionC is enabled, this sends an edit command to WASM
   * and waits for the response before updating local state.
   */
  private insertText(text: string): void {
    // 🔧 FIX (2026-01-20): Reset sticky column on any text modification (MS Word behavior)
    this.desiredVisualX = null;

    // ✅ OPTION C: Use WASM as single source of truth
    if (this.config.useOptionC && this.config.onEditCommand) {
      this.insertTextOptionC(text);
      return;
    }

    // ✅ Original implementation (optimistic updates)
    this.insertTextOptimistic(text);
  }
  
  /**
   * 🆕 OPTION C: Insert text via WASM (single source of truth)
   * 
   * HYBRID APPROACH: Optimistic rendering + WASM as source of truth
   * - Text and cursor are updated IMMEDIATELY for responsive UI
   * - WASM processes in background and reconciles state
   * - This gives instant feedback while WASM catches up
   */
  private insertTextOptionC(text: string): void {
    InputLogger.editing.insert(text, { position: this.currentState.selection.focus });
    const cursorPosition = this.currentState.selection.focus;
    const { start, end } = normalizeSelection(this.currentState.selection);
    const currentText = this.currentState.text;
    
    // ✅ FULLY OPTIMISTIC: Update text AND cursor immediately
    let newText: string;
    let newCursorPosition: number;
    
    if (start !== end) {
      // Selection replacement - compute new text optimistically
      const mapping = this.getGraphemeMapping();
      let utf16Start: number;
      let utf16End: number;
      
      if (mapping.length > 0) {
        utf16Start = mapping[start] ?? start;
        utf16End = mapping[end] ?? currentText.length;
      } else {
        utf16Start = start;
        utf16End = end;
      }
      
      newText = currentText.slice(0, utf16Start) + text + currentText.slice(utf16End);
      const graphemeCount = this.countGraphemes(text);
      newCursorPosition = start + graphemeCount;
    } else {
      // Simple insert - compute new text optimistically
      newText = currentText.slice(0, cursorPosition) + text + currentText.slice(cursorPosition);
      const graphemeCount = this.countGraphemes(text);
      newCursorPosition = cursorPosition + graphemeCount;
    }
    
    // ✅ UPDATE STATE IMMEDIATELY (optimistic)
    this.currentState.text = newText;
    this.currentState.cursorPosition = newCursorPosition;
    this.currentState.selection = { anchor: newCursorPosition, focus: newCursorPosition };
    
    // Track for echo-back prevention
    (this as any).lastSentText = newText;
    
    // ✅ NOTIFY REACT IMMEDIATELY (triggers render with optimistic state)
    this.config.onTextChange(newText, this.currentState.selection);
    
    // Emit cursor change immediately for responsive caret
    this.emitCursorChange(newCursorPosition);
    
    // Build the edit command for WASM
    let command: EditCommand;
    
    if (start !== end) {
      command = {
        type: 'replace',
        position: start,
        text,
        deleteCount: end - start
      };
    } else {
      command = {
        type: 'insert',
        position: cursorPosition,
        text
      };
    }
    
    // ✅ QUEUE: Send to WASM in background (reconciliation)
    // WASM will process and we'll reconcile if there's a mismatch
    this.optionCPendingOperations++;
    this.optionCOperationQueue = this.optionCOperationQueue.then(async () => {
      try {
        const result = await this.config.onEditCommand!(command);
        
        if (result.success) {
          // ✅ RECONCILE: Only reconcile when this is the LAST pending operation
          // If more operations are queued, the optimistic state is ahead of WASM
          // and we should NOT overwrite it with WASM's older state
          const isLastOperation = this.optionCPendingOperations === 1;
          
          if (isLastOperation) {
            // Safe to reconcile - no more operations pending
            if (result.newText !== this.currentState.text || 
                result.newCursorPosition !== this.currentState.selection.focus) {
              DEBUG_ENABLED && console.log('🔄 [Option C] Reconciling with WASM (last op):', {
                optimisticText: this.currentState.text.length,
                wasmText: result.newText.length,
                optimisticCursor: this.currentState.selection.focus,
                wasmCursor: result.newCursorPosition
              });
              
              this.currentState.text = result.newText;
              this.currentState.cursorPosition = result.newCursorPosition;
              this.currentState.selection = { 
                anchor: result.newCursorPosition, 
                focus: result.newCursorPosition 
              };
              
              (this as any).lastSentText = result.newText;
              this.config.onTextChange(result.newText, this.currentState.selection);
              this.emitCursorChange(result.newCursorPosition);
            }
          }
          // If not last operation, skip reconciliation - optimistic state is ahead
        } else {
          // ⚠️ WASM FAILED - Rollback optimistic state to WASM's actual state
          // This prevents desync where frontend thinks text is shorter but WASM still has original
          InputLogger.editError('option-c-insert-failed', new Error(result.error || 'Unknown error'));
          InputLogger.edit.rollback('insert', {
            optimisticText: this.currentState.text.length,
            wasmText: result.newText.length
          });
          this.currentState.text = result.newText;
          this.currentState.cursorPosition = result.newCursorPosition;
          this.currentState.selection = { 
            anchor: result.newCursorPosition, 
            focus: result.newCursorPosition 
          };
          (this as any).lastSentText = result.newText;
          this.config.onTextChange(result.newText, this.currentState.selection);
          this.emitCursorChange(result.newCursorPosition);
        }
      } catch (error) {
        InputLogger.editError('option-c-insert-threw', error as Error);
      } finally {
        this.optionCPendingOperations--;
      }
    });
    
    // Record event
    this.recordEvent({
      type: 'insert',
      position: cursorPosition,
      text
    });
  }
  
  /**
   * Original insertText implementation (optimistic updates)
   */
  private insertTextOptimistic(text: string): void {
    // ✅ REMOVED: pendingStateUpdate guard was causing keystrokes to be dropped during rapid typing
    // The internal state (this.currentState) is updated synchronously, so we don't need to wait

    const { text: currentText } = this.currentState;
    const cursorPosition = this.currentState.selection.focus;

    // 🐛 DEBUG: Enhanced logging for spacebar issue
    const isSpace = text === ' ';
    if (isSpace) {
      DEBUG_ENABLED && console.log('⌨️ 🐛 [SPACEBAR DEBUG] Starting insertion:', {
        insertedText: JSON.stringify(text),
        cursorPosition,
        textBefore: currentText.slice(Math.max(0, cursorPosition - 10), cursorPosition),
        textAfter: currentText.slice(cursorPosition, Math.min(currentText.length, cursorPosition + 10)),
        currentTextLength: currentText.length
      });
    }

    DEBUG_ENABLED && console.log('🔍 insertText called:', {text: JSON.stringify(text), cursorPosition, currentTextLength: currentText.length});

    // Check length limit
    if (this.config.maxLength && currentText.length + text.length > this.config.maxLength) {
      return;
    }

    // Handle selection replacement
    let newText = currentText;
    let newCursorPosition = cursorPosition;

    const { start, end } = normalizeSelection(this.currentState.selection);
    if (start !== end) {
      // Replace selection with new text
      // ✅ CRITICAL FIX: Convert grapheme indices to UTF-16 character indices
      // start and end are grapheme indices, but slice() needs UTF-16 indices
      const mapping = this.getGraphemeMapping();
      
      let utf16Start: number;
      let utf16End: number;
      
      if (mapping.length > 0) {
        utf16Start = mapping[start] ?? start;
        utf16End = mapping[end] ?? currentText.length;
      } else {
        // Fallback: assume 1:1 mapping (ASCII only)
        InputLogger.edit.graphemeFallback('insertText');
        utf16Start = start;
        utf16End = end;
      }
      
      newText = currentText.slice(0, utf16Start) + text + currentText.slice(utf16End);
      // ✅ Use grapheme count for cursor positioning
      const graphemeCount = this.countGraphemes(text);
      newCursorPosition = start + graphemeCount;
      this.clearSelection();
    } else {
      // Insert at cursor
      newText = currentText.slice(0, cursorPosition) + text + currentText.slice(cursorPosition);
      
      // ✅ Grapheme-aware cursor advancement
      // Count graphemes in inserted text, not UTF-16 code units
      // For simple ASCII/single chars, grapheme count ≈ text.length
      // For complex text (emoji, combining marks, multi-line), we use grapheme segmentation
      const graphemeCount = this.countGraphemes(text);
      newCursorPosition = cursorPosition + graphemeCount;
      
      // 🐛 DEBUG: Enhanced logging for spacebar cursor positioning
      if (isSpace) {
        DEBUG_ENABLED && console.log('⌨️ 🐛 [SPACEBAR DEBUG] Cursor advancement:', {
          utf16Length: text.length,
          graphemeCount,
          oldPosition: cursorPosition,
          newPosition: newCursorPosition,
          delta: newCursorPosition - cursorPosition,
          expectedDelta: 1,
          match: (newCursorPosition - cursorPosition) === 1 ? '✅' : '❌'
        });
      }
      
      DEBUG_ENABLED && console.log('🔍 [insertText] Cursor advancement:', {
        insertedText: text.length > 20 ? text.slice(0, 20) + '...' : text,
        utf16Length: text.length,
        graphemeCount,
        oldPosition: cursorPosition,
        newPosition: newCursorPosition
      });
    }

    // 🐛 DEBUG: Enhanced logging for spacebar result
    if (isSpace) {
      DEBUG_ENABLED && console.log('⌨️ 🐛 [SPACEBAR DEBUG] Final state before updateText:', {
        newTextLength: newText.length,
        newCursorPosition,
        textAroundCursor: {
          before: newText.slice(Math.max(0, newCursorPosition - 10), newCursorPosition),
          after: newText.slice(newCursorPosition, Math.min(newText.length, newCursorPosition + 10))
        }
      });
    }

    DEBUG_ENABLED && console.log('🔍 insertText result:', {newTextLength: newText.length, newCursorPosition, textInserted: text === '\n' ? 'newline' : text});

    this.updateText(newText, newCursorPosition);

    // Record event
    this.recordEvent({
      type: 'insert',
      position: cursorPosition,
      text
    });
  }

  /**
   * Count grapheme clusters in a string
   * Uses Intl.Segmenter for accurate grapheme segmentation
   */
  private countGraphemes(text: string): number {
    // Use Intl.Segmenter if available (modern browsers - ES2022)
    // @ts-ignore - Intl.Segmenter is a newer API not in all TypeScript versions
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter !== 'undefined') {
      // @ts-ignore
      const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text));
      return segments.length;
    }
    
    // Fallback: Use simple character count
    // This works for most cases but may be inaccurate for complex graphemes
    return text.length;
  }

  /**
   * Handle backspace key
   * ✅ MANDATORY: Grapheme-aware deletion using WASM grapheme boundaries
   * ✅ OPTION C: When enabled, sends delete command to WASM
   * ✅ MS WORD PARITY: After deleting newline, cursor lands at end of visible content
   */
  private handleBackspace(): void {
    // 🔧 FIX (2026-01-20): Reset sticky column on any text modification (MS Word behavior)
    this.desiredVisualX = null;

    const { text, selection } = this.currentState;
    const cursorGraphemeIndex = selection.focus;
    InputLogger.editing.delete(1, 'backward', { position: cursorGraphemeIndex });

    const { start, end } = normalizeSelection(selection);
    if (start !== end) {
      // Delete selection (range already in grapheme indices)
      this.deleteSelection();
      return;
    }

    if (cursorGraphemeIndex <= 0) {
      return; // Nothing to delete
    }

    // ✅ OPTION C: Use WASM as single source of truth
    if (this.config.useOptionC && this.config.onEditCommand) {
      this.handleBackspaceOptionC(cursorGraphemeIndex);
      return;
    }
    
    // ✅ Original implementation (optimistic updates)
    const mapping = this.getGraphemeMapping();
    
    // ✅ Graceful fallback during initialization
    if (mapping.length === 0) {
      InputLogger.edit.graphemeFallback('handleBackspace');
      const newText = text.slice(0, cursorGraphemeIndex - 1) + text.slice(cursorGraphemeIndex);
      // MS Word parity: adjust cursor if deleting newline
      const charDeleted = text.charAt(cursorGraphemeIndex - 1);
      let newCursorPos = cursorGraphemeIndex - 1;
      if (charDeleted === '\n') {
        while (newCursorPos > 0 && newText.charAt(newCursorPos - 1) === '\n') {
          newCursorPos--;
        }
      }
      this.updateText(newText, newCursorPos);
      return;
    }
    
    // ✅ Use WASM grapheme boundaries
    const prevGraphemeIndex = cursorGraphemeIndex - 1;
    const utf16Start = mapping[prevGraphemeIndex];
    const utf16End = mapping[cursorGraphemeIndex];
    
    // ✅ FIX: Handle undefined utf16End (mapping might not have final position)
    // If utf16End is undefined, it means we're at the end of the text
    const actualUtf16End = utf16End !== undefined ? utf16End : text.length;
    
    const newText = text.slice(0, utf16Start) + text.slice(actualUtf16End);
    
    // ✅ MS WORD PARITY: After deleting newline, cursor lands at end of visible content
    const charDeleted = text.charAt(utf16Start);
    let newCursorPosition = prevGraphemeIndex;
    if (charDeleted === '\n') {
      // Scan backwards to find the position after the last visible character
      while (newCursorPosition > 0 && newText.charAt(newCursorPosition - 1) === '\n') {
        newCursorPosition--;
      }
      DEBUG_ENABLED && console.log('🔄 [Backspace non-OptionC] MS Word parity adjustment:', {
        originalPos: prevGraphemeIndex,
        adjustedPos: newCursorPosition
      });
    }
    
    this.updateText(newText, newCursorPosition);
    
    this.recordEvent({
      type: 'delete',
      position: prevGraphemeIndex,
      length: actualUtf16End - utf16Start
    });
  }
  
  /**
   * 🆕 OPTION C: Handle backspace via WASM (single source of truth)
   * 
   * HYBRID APPROACH: Optimistic rendering + WASM as source of truth
   * 
   * ✅ MS WORD PARITY: When backspacing across a paragraph boundary (deleting \n),
   * the cursor should land at the end of the previous line's content, NOT after
   * the remaining newline character. This matches MS Word behavior.
   */
  private handleBackspaceOptionC(cursorGraphemeIndex: number): void {
    const currentText = this.currentState.text;
    let deletePosition = cursorGraphemeIndex - 1;
    
    // ✅ FULLY OPTIMISTIC: Update text AND cursor immediately
    // For simple ASCII, we can compute the new text optimistically
    const mapping = this.getGraphemeMapping();
    let utf16DeletePos: number;
    
    if (mapping.length > 0) {
      utf16DeletePos = mapping[deletePosition] ?? deletePosition;
    } else {
      utf16DeletePos = deletePosition;
    }
    
    // Delete single character
    let deleteCount = 1;
    const charBeingDeleted = currentText.charAt(utf16DeletePos);
    
    // Delete the character(s)
    const newText = currentText.slice(0, utf16DeletePos) + currentText.slice(utf16DeletePos + deleteCount);
    
    // ✅ MS WORD PARITY: Cursor lands at deletePosition (where the deleted char was).
    // This moves to the previous line, which may be empty or have content.
    const newCursorPosition = deletePosition;
    
    DEBUG_ENABLED && console.log('🔄 [Backspace] Delete:', {
      deletePosition,
      deleteCount,
      charDeleted: charBeingDeleted === '\n' ? 'NEWLINE' : `'${charBeingDeleted}'`,
      newCursorPosition,
      newTextPreview: newText.slice(0, 20).replace(/\n/g, '\\n')
    });
    
    // ✅ UPDATE STATE IMMEDIATELY (optimistic)
    this.currentState.text = newText;
    this.currentState.cursorPosition = newCursorPosition;
    this.currentState.selection = { anchor: newCursorPosition, focus: newCursorPosition };
    
    // Track for echo-back prevention
    (this as any).lastSentText = newText;
    
    // ✅ NOTIFY REACT IMMEDIATELY (triggers render with optimistic state)
    this.config.onTextChange(newText, this.currentState.selection);
    
    // Emit cursor change immediately for responsive caret
    this.emitCursorChange(newCursorPosition);
    
    const command: EditCommand = {
      type: 'delete',
      position: deletePosition,
      deleteCount: deleteCount
    };
    
    // ✅ QUEUE: Send to WASM in background (reconciliation)
    this.optionCPendingOperations++;
    this.optionCOperationQueue = this.optionCOperationQueue.then(async () => {
      try {
        const result = await this.config.onEditCommand!(command);
        
        if (result.success) {
          // ✅ RECONCILE: Only reconcile when this is the LAST pending operation
          const isLastOperation = this.optionCPendingOperations === 1;
          
          if (isLastOperation) {
            // ✅ Simple reconciliation: use WASM cursor position directly
            // (no more cursor adjustment - MS Word parity is just deleting one char at a time)
            const finalCursorPosition = result.newCursorPosition;
            
            if (result.newText !== this.currentState.text || 
                finalCursorPosition !== this.currentState.selection.focus) {
              DEBUG_ENABLED && console.log('🔄 [Option C] Reconciling backspace with WASM (last op):', {
                optimisticText: this.currentState.text.length,
                wasmText: result.newText.length,
                optimisticCursor: this.currentState.selection.focus,
                wasmCursor: result.newCursorPosition,
                finalCursorPosition
              });
              
              this.currentState.text = result.newText;
              this.currentState.cursorPosition = finalCursorPosition;
              this.currentState.selection = { 
                anchor: finalCursorPosition, 
                focus: finalCursorPosition 
              };
              
              (this as any).lastSentText = result.newText;
              this.config.onTextChange(result.newText, this.currentState.selection);
              this.emitCursorChange(finalCursorPosition);
            }
          }
        } else {
          // ⚠️ WASM FAILED - Rollback optimistic state to WASM's actual state
          InputLogger.editError('option-c-backspace-failed', new Error(result.error || 'Unknown error'));
          InputLogger.edit.rollback('backspace', {
            optimisticText: this.currentState.text.length,
            wasmText: result.newText.length
          });
          this.currentState.text = result.newText;
          this.currentState.cursorPosition = result.newCursorPosition;
          this.currentState.selection = { 
            anchor: result.newCursorPosition, 
            focus: result.newCursorPosition 
          };
          (this as any).lastSentText = result.newText;
          this.config.onTextChange(result.newText, this.currentState.selection);
          this.emitCursorChange(result.newCursorPosition);
        }
      } catch (error) {
        InputLogger.editError('option-c-backspace-threw', error as Error);
      } finally {
        this.optionCPendingOperations--;
      }
    });
    
    this.recordEvent({
      type: 'delete',
      position: deletePosition,
      length: 1
    });
  }

  /**
   * Handle delete key
   * ✅ OPTION C: When enabled, sends delete command to WASM
   */
  private handleDelete(): void {
    // 🔧 FIX (2026-01-20): Reset sticky column on any text modification (MS Word behavior)
    this.desiredVisualX = null;

    const cursorPosition = this.currentState.selection.focus;
    InputLogger.editing.delete(1, 'forward', { position: cursorPosition });

    // ✅ OPTION C: Use WASM as single source of truth
    if (this.config.useOptionC && this.config.onEditCommand) {
      this.handleDeleteOptionC();
      return;
    }
    
    // ✅ Original implementation (optimistic updates)
    const { text, selection } = this.currentState;

    const { start, end } = normalizeSelection(selection);
    if (start !== end) {
      // Delete selection - use same approach as Cut for consistency
      const result = applyDeleteRange(this.currentState, start, end);
      this.updateState(result.state);
      if (result.op) this.pushToHistory(result.op);
    } else if (cursorPosition < text.length) {
      // Delete character at cursor - use applyDeleteRange for consistency
      const result = applyDeleteRange(this.currentState, cursorPosition, cursorPosition + 1);
      this.updateState(result.state);
      if (result.op) this.pushToHistory(result.op);
    }
  }
  
  /**
   * 🆕 OPTION C: Handle Delete key via WASM (single source of truth)
   * 
   * HYBRID APPROACH: Optimistic rendering + WASM as source of truth
   * - Text and cursor are updated IMMEDIATELY for responsive UI
   * - WASM processes in background and reconciles state
   */
  private handleDeleteOptionC(): void {
    const { text: currentText, selection } = this.currentState;
    const cursorPosition = selection.focus;
    const { start, end } = normalizeSelection(selection);
    
    // Determine what to delete
    let deleteStart: number;
    let deleteCount: number;
    
    if (start !== end) {
      // Delete selection
      deleteStart = start;
      deleteCount = end - start;
    } else if (cursorPosition < this.countGraphemes(currentText)) {
      // Delete character at cursor (forward delete)
      deleteStart = cursorPosition;
      deleteCount = 1;
    } else {
      // At end of document, nothing to delete
      InputLogger.deleteKey.noop('cursor at end of document', cursorPosition, this.countGraphemes(currentText));
      return;
    }
    
    // ✅ OPTIMISTIC: Compute new text immediately
    const mapping = this.getGraphemeMapping();
    let utf16Start: number;
    let utf16End: number;
    
    if (mapping.length > 0) {
      utf16Start = mapping[deleteStart] ?? deleteStart;
      utf16End = mapping[deleteStart + deleteCount] ?? currentText.length;
    } else {
      utf16Start = deleteStart;
      utf16End = deleteStart + deleteCount;
    }
    
    // Delete single character
    const charBeingDeleted = currentText.charAt(utf16Start);
    
    // 🔬 DIAGNOSTIC: Log DELETE key with full context
    InputLogger.deleteKey.pressed(
      deleteStart,
      this.countGraphemes(currentText),
      charBeingDeleted,
      charBeingDeleted.charCodeAt(0),
      { utf16Start, utf16End, deleteCount }
    );
    
    const newText = currentText.slice(0, utf16Start) + currentText.slice(utf16End);
    const newCursorPosition = deleteStart; // Cursor stays at delete position
    
    // ✅ UPDATE STATE IMMEDIATELY (optimistic)
    this.currentState.text = newText;
    this.currentState.cursorPosition = newCursorPosition;
    this.currentState.selection = { anchor: newCursorPosition, focus: newCursorPosition };
    
    // Track for echo-back prevention
    (this as any).lastSentText = newText;
    
    // ✅ NOTIFY REACT IMMEDIATELY (triggers render with optimistic state)
    this.config.onTextChange(newText, this.currentState.selection);
    
    // Emit cursor change immediately for responsive caret
    this.emitCursorChange(newCursorPosition);
    
    const command: EditCommand = {
      type: 'delete',
      position: deleteStart,
      deleteCount
    };
    
    // ✅ QUEUE: Send to WASM in background (reconciliation)
    this.optionCPendingOperations++;
    const beforeTextLen = this.countGraphemes(currentText);
    const beforeCursor = deleteStart;
    
    this.optionCOperationQueue = this.optionCOperationQueue.then(async () => {
      try {
        const result = await this.config.onEditCommand!(command);
        
        // 🔬 DIAGNOSTIC: Log DELETE result from WASM
        InputLogger.deleteKey.result(
          beforeCursor,
          result.newCursorPosition,
          beforeTextLen,
          result.newText.length,
          charBeingDeleted,
          { success: result.success, isLastOp: this.optionCPendingOperations === 1 }
        );
        
        if (result.success) {
          // ✅ RECONCILE: Only reconcile when this is the LAST pending operation
          const isLastOperation = this.optionCPendingOperations === 1;
          
          if (isLastOperation) {
            if (result.newText !== this.currentState.text || 
                result.newCursorPosition !== this.currentState.selection.focus) {
              DEBUG_ENABLED && console.log('🔄 [Option C] Reconciling delete with WASM (last op):', {
                optimisticText: this.currentState.text.length,
                wasmText: result.newText.length,
                optimisticCursor: this.currentState.selection.focus,
                wasmCursor: result.newCursorPosition
              });
              
              this.currentState.text = result.newText;
              this.currentState.cursorPosition = result.newCursorPosition;
              this.currentState.selection = { 
                anchor: result.newCursorPosition, 
                focus: result.newCursorPosition 
              };
              
              (this as any).lastSentText = result.newText;
              this.config.onTextChange(result.newText, this.currentState.selection);
              this.emitCursorChange(result.newCursorPosition);
            }
          }
        } else {
          // ⚠️ WASM FAILED - Rollback optimistic state to WASM's actual state
          InputLogger.editError('option-c-delete-failed', new Error(result.error || 'Unknown error'));
          InputLogger.edit.rollback('delete', {
            optimisticText: this.currentState.text.length,
            wasmText: result.newText.length
          });
          this.currentState.text = result.newText;
          this.currentState.cursorPosition = result.newCursorPosition;
          this.currentState.selection = { 
            anchor: result.newCursorPosition, 
            focus: result.newCursorPosition 
          };
          (this as any).lastSentText = result.newText;
          this.config.onTextChange(result.newText, this.currentState.selection);
          this.emitCursorChange(result.newCursorPosition);
        }
      } catch (error) {
        InputLogger.editError('option-c-delete-threw', error as Error);
      } finally {
        this.optionCPendingOperations--;
      }
    });
    
    this.recordEvent({
      type: 'delete',
      position: deleteStart,
      length: deleteCount
    });
  }

  /**
   * Handle enter key - creates a new paragraph (MS Word model)
   *
   * Inserts carriage return (\r) which creates a paragraph break.
   * This follows the MS Word model where:
   * - ENTER (\r) = New paragraph (completes current paragraph)
   * - Shift+Enter (\n) = Soft line break (stays in same paragraph)
   *
   * Provides:
   * - Clean single-line spacing (no blank lines between paragraphs)
   * - Caret advances to first position of the new paragraph
   * - Proper paragraph detection in WASM (\r = paragraph boundary)
   *
   * Note: Pressing ENTER twice creates two paragraphs (one empty), resulting
   * in a visual blank line between content.
   */
  private handleEnter(): void {
    const beforePos = this.currentState.selection.focus;
    const mapping = this.config.getGraphemeToUtf16?.() ?? [];

    DEBUG_ENABLED && console.log('🔍 [ENTER] Before:', {
      cursorPosition: beforePos,
      caretMapLength: mapping.length,
      textLength: this.currentState.text.length
    });

    // ✅ PARAGRAPH BREAK: Carriage return creates new paragraph (MS Word model)
    // WASM's detect_paragraphs() uses \r as paragraph boundary
    // NOTE: \n (line feed) is soft line break - stays in same paragraph
    this.insertText('\r');

    DEBUG_ENABLED && console.log('🔍 [ENTER] After:', {
      cursorPosition: this.currentState.selection.focus,
      delta: this.currentState.selection.focus - beforePos,
      expectedDelta: 1,  // Carriage return = +1 grapheme
      match: (this.currentState.selection.focus - beforePos) === 1,
      note: 'Paragraph break (\\r) - new paragraph in document'
    });

    // ✅ Immediately update caret position after Enter
    // The WASM layout will provide the precise visual position via caret_map
    this.emitCursorChange(this.currentState.selection.focus);

    this.recordEvent({
      type: 'enter',
      position: beforePos
    });
  }

  /**
   * Handle soft line break (Shift+Enter) - stays in SAME paragraph
   *
   * Inserts line feed (\n) for visual line break without creating a new
   * paragraph. This follows the MS Word model where:
   * - ENTER (\r) = New paragraph
   * - Shift+Enter (\n) = Soft line break (same paragraph, new line)
   *
   * Use for:
   * - Multi-line addresses
   * - Poetry/verse formatting
   * - Keeping related lines in same paragraph
   */
  private handleSoftLineBreak(): void {
    const beforePos = this.currentState.selection.focus;

    DEBUG_ENABLED && console.log('🔍 [SOFT BREAK] Before:', {
      cursorPosition: beforePos,
      textLength: this.currentState.text.length
    });

    // ✅ SOFT LINE BREAK: Line feed (\n) stays in same paragraph (MS Word model)
    // WASM does NOT treat \n as paragraph boundary - it's just a visual line break
    this.insertText('\n');

    DEBUG_ENABLED && console.log('🔍 [SOFT BREAK] After:', {
      cursorPosition: this.currentState.selection.focus,
      delta: this.currentState.selection.focus - beforePos,
      expectedDelta: 1,
      note: 'Soft line break (\\n) - same paragraph, new line'
    });

    this.emitCursorChange(this.currentState.selection.focus);

    this.recordEvent({
      type: 'softLineBreak',
      position: beforePos
    });
  }

  /**
   * Handle page break (Ctrl+Enter)
   *
   * Inserts a Form Feed character (\f) which WASM pagination recognizes
   * as a forced page break. This creates a new page regardless of
   * remaining space on the current page.
   *
   * The Form Feed character (U+000C) is:
   * - A standard ASCII control character (code 12)
   * - Historically used for page breaks in printing
   * - Rendered as a visual page break indicator in the canvas
   */
  private handlePageBreak(): void {
    const beforePos = this.currentState.selection.focus;

    InputLogger.pageBreakInsert(beforePos);

    // Insert Form Feed character - WASM will recognize this as a page break
    // We also add a carriage return after it to ensure proper paragraph separation
    this.insertText('\f\r');

    InputLogger.pageBreakInsert(this.currentState.selection.focus, {
      delta: this.currentState.selection.focus - beforePos,
      note: 'Page break inserted - WASM should force new page'
    });

    this.emitCursorChange(this.currentState.selection.focus);

    this.recordEvent({
      type: 'pageBreak',
      position: beforePos
    });
  }

  /**
   * Delete current selection
   * ✅ FIXED: Use grapheme-to-UTF16 mapping for correct string slicing
   * ✅ OPTION C: When enabled, sends delete command to WASM
   */
  private deleteSelection(): void {
    // 🔧 FIX (2026-01-20): Reset sticky column on any text modification (MS Word behavior)
    this.desiredVisualX = null;

    const { text, selection } = this.currentState;
    const { start, end } = normalizeSelection(selection);
    if (start === end) return;

    // ✅ OPTION C: Use WASM as single source of truth
    if (this.config.useOptionC && this.config.onEditCommand) {
      this.deleteSelectionOptionC(start, end);
      return;
    }

    // ✅ Original implementation (optimistic updates)
    // ✅ CRITICAL FIX: Convert grapheme indices to UTF-16 character indices
    // start and end are grapheme indices, but text.slice() needs UTF-16 indices
    const mapping = this.getGraphemeMapping();
    
    if (mapping.length === 0) {
      // Fallback: assume 1:1 mapping (ASCII only - may be incorrect for multi-byte chars)
      InputLogger.edit.graphemeFallback('deleteSelection');
      const newText = text.slice(0, start) + text.slice(end);
      this.updateText(newText, start);
      this.clearSelection();
      return;
    }
    
    // Get UTF-16 positions from grapheme mapping
    const utf16Start = mapping[start] ?? start;
    const utf16End = mapping[end] ?? text.length;
    
    const newText = text.slice(0, utf16Start) + text.slice(utf16End);
    this.updateText(newText, start);
    this.clearSelection();

    this.recordEvent({
      type: 'delete',
      position: start,
      length: end - start  // Grapheme count deleted
    });
  }
  
  /**
   * 🆕 OPTION C: Delete selection via WASM (single source of truth)
   * 
   * HYBRID APPROACH: Optimistic rendering + WASM as source of truth
   */
  private deleteSelectionOptionC(start: number, end: number): void {
    const currentText = this.currentState.text;
    
    // ✅ FULLY OPTIMISTIC: Update text AND cursor immediately
    const mapping = this.getGraphemeMapping();
    let utf16Start: number;
    let utf16End: number;
    
    if (mapping.length > 0) {
      utf16Start = mapping[start] ?? start;
      utf16End = mapping[end] ?? currentText.length;
    } else {
      utf16Start = start;
      utf16End = end;
    }
    
    const newText = currentText.slice(0, utf16Start) + currentText.slice(utf16End);
    const newCursorPosition = start;

    // ✅ UPDATE STATE IMMEDIATELY (optimistic)
    this.currentState.text = newText;
    this.currentState.cursorPosition = newCursorPosition;
    this.currentState.selection = { anchor: newCursorPosition, focus: newCursorPosition };

    // 🔧 FIX (2026-01-20): Clear visual highlight to prevent selection residue after delete
    this.updateSelectionHighlights();

    // Track for echo-back prevention
    (this as any).lastSentText = newText;

    // ✅ NOTIFY REACT IMMEDIATELY (triggers render with optimistic state)
    this.config.onTextChange(newText, this.currentState.selection);

    // Emit cursor change immediately for responsive caret
    this.emitCursorChange(newCursorPosition);

    const command: EditCommand = {
      type: 'delete',
      position: start,
      deleteCount: end - start
    };
    
    // ✅ QUEUE: Send to WASM in background (reconciliation)
    this.optionCPendingOperations++;
    this.optionCOperationQueue = this.optionCOperationQueue.then(async () => {
      try {
        const result = await this.config.onEditCommand!(command);
        
        if (result.success) {
          // ✅ RECONCILE: Only update if WASM result differs from optimistic
          if (result.newText !== this.currentState.text || 
              result.newCursorPosition !== this.currentState.selection.focus) {
            DEBUG_ENABLED && console.log('🔄 [Option C] Reconciling delete selection with WASM:', {
              optimisticText: this.currentState.text.length,
              wasmText: result.newText.length,
              optimisticCursor: this.currentState.selection.focus,
              wasmCursor: result.newCursorPosition
            });
            
            this.currentState.text = result.newText;
            this.currentState.cursorPosition = result.newCursorPosition;
            this.currentState.selection = { 
              anchor: result.newCursorPosition, 
              focus: result.newCursorPosition 
            };
            
            (this as any).lastSentText = result.newText;
            this.config.onTextChange(result.newText, this.currentState.selection);
            this.emitCursorChange(result.newCursorPosition);
          }
        } else {
          // ⚠️ WASM FAILED - Rollback optimistic state to WASM's actual state
          InputLogger.editError('option-c-delete-selection-failed', new Error(result.error || 'Unknown error'));
          InputLogger.edit.rollback('delete-selection', {
            optimisticText: this.currentState.text.length,
            wasmText: result.newText.length
          });
          this.currentState.text = result.newText;
          this.currentState.cursorPosition = result.newCursorPosition;
          this.currentState.selection = { 
            anchor: result.newCursorPosition, 
            focus: result.newCursorPosition 
          };
          (this as any).lastSentText = result.newText;
          this.config.onTextChange(result.newText, this.currentState.selection);
          this.emitCursorChange(result.newCursorPosition);
        }
      } catch (error) {
        InputLogger.editError('option-c-delete-selection-threw', error as Error);
      } finally {
        this.optionCPendingOperations--;
      }
    });
    
    this.recordEvent({
      type: 'delete',
      position: start,
      length: end - start
    });
  }

  /**
   * Move cursor by character
   * ✅ MANDATORY: Grapheme-aware navigation using WASM caret_map indices
   */
  private moveCursor(delta: number, extendSelection: boolean = false): void {
    const currentPosition = this.currentState.selection.focus;
    
    // ✅ Clamp to grapheme count from WASM mapping (graceful fallback)
    // ✅ FIX: Max cursor position is grapheme count (N+1 positions for N graphemes)
    // For 9 graphemes, positions are 0-9, so max is 9 (not 8)
    const mapping = this.getGraphemeMapping();
    const maxGraphemeIndex = mapping.length > 0 ? mapping.length : this.currentState.text.length;
    
    const newPosition = Math.max(0, Math.min(maxGraphemeIndex, currentPosition + delta));

    if (extendSelection) {
      this.extendSelection(newPosition);
    } else {
      this.setCursorPosition(newPosition);
    }
  }

  /**
   * Move cursor by line (simplified - treats \n as line breaks)
   */
  private moveCursorLine(lineDelta: number, extendSelection: boolean = false): void {
    const text = this.currentState.text;
    const currentPos = this.currentState.selection.focus;

    // Find current line
    const lines = text.split('\n');
    let currentLineStart = 0;
    let currentLineIndex = 0;
    let charInLine = 0;

    for (let i = 0; i < lines.length; i++) {
      const lineEnd = currentLineStart + lines[i].length;
      if (currentPos <= lineEnd) {
        currentLineIndex = i;
        charInLine = currentPos - currentLineStart;
        break;
      }
      currentLineStart = lineEnd + 1; // +1 for \n
    }

    // Calculate target line
    const targetLineIndex = Math.max(0, Math.min(lines.length - 1, currentLineIndex + lineDelta));
    const targetLineStart = lines.slice(0, targetLineIndex).reduce((sum, line) => sum + line.length + 1, 0);
    const targetCharInLine = Math.min(charInLine, lines[targetLineIndex].length);
    const newPosition = targetLineStart + targetCharInLine;

    if (extendSelection) {
      this.extendSelection(newPosition);
    } else {
      this.setCursorPosition(newPosition);
    }
  }

  /**
   * Move cursor to line start
   */
  private moveCursorToLineStart(extendSelection: boolean = false): void {
    const text = this.currentState.text;
    const currentPos = this.currentState.selection.focus;

    // Find start of current line
    let lineStart = currentPos;
    while (lineStart > 0 && text[lineStart - 1] !== '\n') {
      lineStart--;
    }

    if (extendSelection) {
      this.extendSelection(lineStart);
    } else {
      this.setCursorPosition(lineStart);
    }
  }

  /**
   * Move cursor to line end
   */
  private moveCursorToLineEnd(extendSelection: boolean = false): void {
    const text = this.currentState.text;
    const currentPos = this.currentState.selection.focus;

    // Find end of current line
    let lineEnd = currentPos;
    while (lineEnd < text.length && text[lineEnd] !== '\n') {
      lineEnd++;
    }

    if (extendSelection) {
      this.extendSelection(lineEnd);
    } else {
      this.setCursorPosition(lineEnd);
    }
  }

  /**
   * Set cursor position
   */
  private setCursorPosition(position: number): void {
    DEBUG_ENABLED && console.log('🎯 setCursorPosition called:', { requested: position, textLength: this.currentState.text.length });
    const clamped = Math.max(0, Math.min(this.currentState.text.length, position));
    InputLogger.caret.move(clamped, { requested: position, textLength: this.currentState.text.length });
    DEBUG_ENABLED && console.log('🎯 setCursorPosition clamped to:', clamped);
    const selection = { anchor: clamped, focus: clamped };
    this.currentState.selection = selection;
    this.currentState.cursorPosition = clamped; // Keep for backward compatibility
    DEBUG_ENABLED && console.log('🎯 setCursorPosition: Calling emitCursorChange with:', clamped);
    // DO NOT call onTextChange here – this is cursor-only
    this.emitCursorChange(clamped);
    DEBUG_ENABLED && console.log('🎯 setCursorPosition: After emitCursorChange, selection.focus is:', this.currentState.selection.focus);
  }

  /**
   * Extend selection to position
   */
  private extendSelection(position: number): void {
    const anchor = this.currentState.selection.anchor;
    const focus = position;

    this.currentState.selection = { anchor, focus };
    this.config.onSelectionChange?.(this.currentState.selection);
    // selection move also implies caret move:
    this.emitCursorChange(position);
  }

  /**
   * Move by word boundaries
   *
   * 🔧 FIX (2026-01-20): Improved word boundary detection to match MS Word behavior:
   * - Uses Unicode-aware character classification via Unicode property escapes
   * - Treats punctuation as word boundaries (stops at punctuation)
   * - Handles accented characters and non-Latin scripts correctly
   *
   * MS Word word boundary rules:
   * - Words are sequences of letters/numbers (Unicode Letter/Number categories)
   * - Punctuation marks are word boundaries
   * - Whitespace is skipped after/before words
   */
  private moveByWord(dir: -1 | 1, extendSelection = false): void {
    const { text } = this.currentState;
    const cursorPosition = this.currentState.selection.focus;
    let i = cursorPosition;

    // Unicode-aware character classification
    // \p{L} = Letter (any language), \p{N} = Number
    // Note: Using try/catch for environments that don't support Unicode property escapes
    let isWordChar: (ch: string) => boolean;
    let isPunctuation: (ch: string) => boolean;

    try {
      // Modern browsers support Unicode property escapes
      const wordRegex = /^[\p{L}\p{N}_]$/u;
      const punctRegex = /^[\p{P}\p{S}]$/u;  // Punctuation or Symbol
      isWordChar = (ch: string) => wordRegex.test(ch);
      isPunctuation = (ch: string) => punctRegex.test(ch);
    } catch {
      // Fallback for older environments - extended ASCII word chars
      isWordChar = (ch: string) => /^[\w\u00C0-\u024F\u1E00-\u1EFF]$/.test(ch);
      isPunctuation = (ch: string) => /^[^\w\s\u00C0-\u024F\u1E00-\u1EFF]$/.test(ch);
    }

    const isSpace = (ch: string) => /^\s$/.test(ch);

    if (dir < 0) {
      // Moving left (Ctrl+Left): Move to start of previous word
      // Skip whitespace first
      while (i > 0 && isSpace(text[i - 1])) i--;

      if (i > 0) {
        const prevChar = text[i - 1];
        if (isPunctuation(prevChar)) {
          // If on punctuation, skip all consecutive punctuation
          while (i > 0 && isPunctuation(text[i - 1])) i--;
        } else if (isWordChar(prevChar)) {
          // Skip word characters
          while (i > 0 && isWordChar(text[i - 1])) i--;
        }
      }
    } else {
      // Moving right (Ctrl+Right): Move to start of next word
      if (i < text.length) {
        const currentChar = text[i];
        if (isWordChar(currentChar)) {
          // Skip current word
          while (i < text.length && isWordChar(text[i])) i++;
        } else if (isPunctuation(currentChar)) {
          // Skip punctuation
          while (i < text.length && isPunctuation(text[i])) i++;
        }
      }
      // Skip whitespace after word/punctuation
      while (i < text.length && isSpace(text[i])) i++;
    }

    if (extendSelection) this.extendSelection(i);
    else this.setCursorPosition(i);
  }

  // ✅ WASM-only accessors with initialization guard
  // During initialization, providers may return empty arrays - this is OK
  // Operations that need data will fail gracefully with warnings
  private getWasmLines(): Line[] {
    const lines = this.config.getWasmLines();
    return lines || [];
  }

  private getCaretMap(): CaretPosition[] {
    const caretMap = this.config.getCaretMap();
    return caretMap || [];
  }

  private getGraphemeMapping(): number[] {
    const mapping = this.config.getGraphemeToUtf16();
    return mapping || [];
  }

  /**
   * 🔧 FIX (2026-01-20): Get number of lines to jump for PageUp/PageDown
   *
   * MS Word behavior: Page navigation jumps roughly one screenful of text.
   * We estimate ~20 lines as a reasonable page size. This could be made
   * configurable via InputHandlerConfig if needed.
   *
   * Note: Ideally this would be based on viewport height / line height,
   * but that would require passing viewport dimensions to the input handler.
   */
  private getPageLineCount(): number {
    // Default page size: 20 lines (typical "screenful" of text)
    // Could be made configurable via this.config.viewportLines if added
    return 20;
  }

  // ✅ Check if WASM data is available for operations
  private hasWasmData(): boolean {
    const lines = this.getWasmLines();
    const caretMap = this.getCaretMap();
    const mapping = this.getGraphemeMapping();
    const hasData = lines.length > 0 && caretMap.length > 0 && mapping.length > 0;
    
    // 🔍 DEBUG: Log WASM data availability for navigation debugging
    if (!hasData) {
      InputLogger.wasmDataUnavailable('hasWasmData', {
        linesCount: lines.length,
        caretMapCount: caretMap.length,
        mappingCount: mapping.length,
        textLength: this.currentState.text.length
      });
    }
    
    return hasData;
  }
  
  private emitCursorChange(index: number) {
    // 🚨 CRITICAL DIAGNOSTIC: Track cursor change emissions
    DEBUG_ENABLED && console.log('🔍 [emitCursorChange] CALLED:', {
      index,
      currentSelectionFocus: this.currentState.selection.focus,
      currentSelectionAnchor: this.currentState.selection.anchor,
      hasCallback: !!this.config.onCursorChange,
      textLength: this.currentState.text.length
    });
    
    if (!this.config.onCursorChange) {
      InputLogger.cursorCallbackMissing(index);
      return;
    }
    
    // ✅ V8.5: Only pass index - Canvas will calculate position from WASM caret_map
    this.config.onCursorChange(index, { x: 0, y: 0, height: 0 });
    
    DEBUG_ENABLED && console.log('🔍 [emitCursorChange] AFTER callback, selection.focus is:', this.currentState.selection.focus);
  }

  // ✅ MANDATORY: Find WASM line containing grapheme position
  // Uses WASM Line type with exclusive end boundary [start, end)
  private findWasmLineAt(position: number, lines: Line[]): { line: Line; index: number } {
    // ✅ CRITICAL: Check for empty lines FIRST before range check
    // Empty lines have start === end, so position must match exactly
    for (let i = 0; i < lines.length; i++) {
      const isEmpty = lines[i].start_grapheme_index === lines[i].end_grapheme_index;
      if (isEmpty && position === lines[i].start_grapheme_index) {
        return { line: lines[i], index: i };
      }
    }

    // Standard case: Find line where position is in [start, end) - exclusive end
    for (let i = 0; i < lines.length; i++) {
      if (position >= lines[i].start_grapheme_index && position < lines[i].end_grapheme_index) {
        return { line: lines[i], index: i };
      }
    }

    // Terminal position: cursor at document end lands on last line's end_grapheme_index
    const lastLine = lines[lines.length - 1];
    if (position === lastLine.end_grapheme_index) {
      return { line: lastLine, index: lines.length - 1 };
    }

    // ✅ GRACEFUL DEGRADATION: Cursor ahead of WASM (typing fast)
    // Return last line instead of crashing - allows arrow keys to work gracefully
    if (position > lastLine.end_grapheme_index) {
      InputLogger.cursorOutOfRange('ahead', position, lastLine.end_grapheme_index);
      return { line: lastLine, index: lines.length - 1 };
    }

    // ✅ CROSS-PAGE NAVIGATION: Cursor outside current page's lines
    // This happens when input handler has lines from a different page than the cursor.
    // Try to get ALL cached lines from the typesetter client.
    const firstLine = lines[0];
    if (position < firstLine.start_grapheme_index) {
      // Try fallback to all cached lines
      const allLines = this.config.getAllCachedLines?.();
      InputLogger.crossPageNavigation(position, 
        `${firstLine.start_grapheme_index}-${lines[lines.length-1].end_grapheme_index}`,
        allLines?.length ?? 0
      );
      
      if (allLines && allLines.length > 0) {
        // Search in all cached lines
        for (let i = 0; i < allLines.length; i++) {
          const isEmpty = allLines[i].start_grapheme_index === allLines[i].end_grapheme_index;
          if (isEmpty && position === allLines[i].start_grapheme_index) {
            InputLogger.cursorFoundInCache(position, i, 'empty');
            return { line: allLines[i], index: i };
          }
          if (position >= allLines[i].start_grapheme_index && position < allLines[i].end_grapheme_index) {
            InputLogger.cursorFoundInCache(position, i, `${allLines[i].start_grapheme_index}-${allLines[i].end_grapheme_index}`);
            return { line: allLines[i], index: i };
          }
        }
        // Check terminal position
        const lastCachedLine = allLines[allLines.length - 1];
        if (position === lastCachedLine.end_grapheme_index) {
          InputLogger.cursorFoundInCache(position, allLines.length - 1, 'terminal');
          return { line: lastCachedLine, index: allLines.length - 1 };
        }
        
        // Log if we couldn't find the position in all cached lines either
        InputLogger.cursorNotFoundInCache(position, allLines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index}]`).join(', '));
      }
      // Fallback if all cached lines also don't contain the position
      InputLogger.cursorOutOfRange('behind', position, firstLine.start_grapheme_index);
      return { line: firstLine, index: 0 };
    }

    // 🔥 CRITICAL FIX: Handle positions in "gaps" between lines (form feed/newline positions)
    // After incremental edits, the cached lines may have gaps where form feed (\f) and 
    // newline (\n) characters live. These positions are valid cursor positions but don't
    // fall within any line's [start, end) range.
    // 
    // Find the line whose end_grapheme_index is closest to (but <= position) and use that.
    // This handles the case where cursor is on a form feed or newline between lines.
    let bestLine: Line | null = null;
    let bestIndex = -1;
    let bestDistance = Infinity;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Check if position is just after this line's end (gap position)
      if (line.end_grapheme_index <= position) {
        const distance = position - line.end_grapheme_index;
        if (distance < bestDistance) {
          bestDistance = distance;
          bestLine = line;
          bestIndex = i;
        }
      }
    }
    
    if (bestLine && bestDistance <= 2) {
      // Position is within 2 graphemes of a line end (likely on \f or \n)
      // Use this line for Home/End navigation
      InputLogger.cursorInGap(position, bestIndex, bestLine.end_grapheme_index, bestDistance);
      return { line: bestLine, index: bestIndex };
    }

    // ✅ MANDATORY: FAIL FAST - no fallback for invalid positions within range
    throw new Error(`[EdgeDocsInput] FATAL: Cursor position ${position} not found in WASM lines. Document may be corrupted.`);
  }
  
  // ✅ MANDATORY: WASM-only visual line navigation (Up/Down arrows)
  // MS Word behavior:
  // - ArrowDown on last line → end of document
  // - ArrowUp on first line → start of document
  // - Otherwise → navigate to corresponding position on adjacent line using PIXEL X (sticky column)
  //
  // 🔧 FIX (2026-01-20): desiredVisualX now stores PIXEL X position, not grapheme offset.
  // This matches MS Word behavior where the cursor tries to maintain the same visual X
  // position when moving between lines, regardless of character widths.
  private moveCursorVisualLine(delta: number, extendSelection = false): void {
    if (!this.hasWasmData()) {
      const lines = this.getWasmLines();
      const caretMap = this.getCaretMap();
      const mapping = this.getGraphemeMapping();
      InputLogger.wasmDataUnavailable('visual-line-navigation', {
        linesCount: lines.length,
        caretMapCount: caretMap.length,
        mappingCount: mapping.length,
        direction: delta > 0 ? 'down' : 'up'
      });
      return;
    }

    const lines = this.getWasmLines();
    const caretMap = this.getCaretMap();
    const pos = this.currentState.selection.focus;
    const text = this.currentState.text;
    const { line: currentLine, index: currentLineIndex } = this.findWasmLineAt(pos, lines);

    // ✅ MS WORD BEHAVIOR: At boundaries, go to start/end of document
    if (delta < 0 && currentLineIndex === 0) {
      // ArrowUp on first line → go to start of document
      DEBUG_ENABLED && console.log('[EdgeDocsInput] ⬆️ ArrowUp on first line → start of document');
      this.desiredVisualX = null; // Reset sticky column
      if (extendSelection) this.extendSelection(0);
      else this.setCursorPosition(0);
      return;
    }

    if (delta > 0 && currentLineIndex === lines.length - 1) {
      // ArrowDown on last line → go to end of document
      const endPos = text.length;
      DEBUG_ENABLED && console.log(`[EdgeDocsInput] ⬇️ ArrowDown on LAST line ${currentLineIndex} → end of document`, {
        currentPos: pos,
        endPos,
        textLength: text.length,
        lastLineRange: `[${currentLine.start_grapheme_index}-${currentLine.end_grapheme_index})`,
        willMove: pos !== endPos
      });
      this.desiredVisualX = null; // Reset sticky column
      if (extendSelection) this.extendSelection(endPos);
      else this.setCursorPosition(endPos);
      return;
    }

    // 🔧 FIX (2026-01-20): Get current cursor's PIXEL X position from caret_map
    // This is the visual X coordinate that we want to preserve when moving vertically
    if (this.desiredVisualX == null) {
      const currentCaretEntry = caretMap.find(c => c.grapheme_index === pos);
      if (currentCaretEntry) {
        this.desiredVisualX = currentCaretEntry.x_fp / 64.0; // Convert from fixed-point to pixels
      } else {
        // 🐛 FIX (2026-01-20): Handle boundary positions more robustly
        // When cursor is at a line boundary (e.g., pos = 83 which is both end of line [75-83)
        // and start of line [83-109)), prioritize finding a caret on the current line.
        const currentLineStart = currentLine.start_grapheme_index;
        const currentLineEnd = currentLine.end_grapheme_index;

        // First, try to find a caret entry within the current line
        let closest: typeof caretMap[0] | undefined;
        let minDist = Infinity;

        for (const c of caretMap) {
          // Prioritize carets within the current line's range
          if (c.grapheme_index >= currentLineStart && c.grapheme_index <= currentLineEnd) {
            const dist = Math.abs(c.grapheme_index - pos);
            if (dist < minDist) {
              minDist = dist;
              closest = c;
            }
          }
        }

        // If no caret found in current line range, fall back to any closest caret
        if (!closest) {
          for (const c of caretMap) {
            const dist = Math.abs(c.grapheme_index - pos);
            if (dist < minDist) {
              minDist = dist;
              closest = c;
            }
          }
        }

        this.desiredVisualX = closest ? closest.x_fp / 64.0 : (currentLine.x_offset_fp / 64.0);

        DEBUG_ENABLED && console.log('[EdgeDocsInput] 📍 desiredVisualX from closest caret:', {
          cursorPos: pos,
          currentLineRange: `[${currentLineStart}-${currentLineEnd})`,
          closestCaretPos: closest?.grapheme_index,
          desiredVisualX: this.desiredVisualX
        });
      }
    }

    const targetLineIndex = Math.max(0, Math.min(lines.length - 1, currentLineIndex + delta));
    const targetLine = lines[targetLineIndex];

    // ✅ DEBUG: Log line details
    DEBUG_ENABLED && console.log('🔍 [DEBUG] Line details:', {
      currentLine: {
        index: currentLineIndex,
        start_grapheme: currentLine.start_grapheme_index,
        end_grapheme: currentLine.end_grapheme_index,
        baseline_y_px: (currentLine.baseline_y_fp / 64).toFixed(2),
        height_px: (currentLine.height_fp / 64).toFixed(2)
      },
      targetLine: {
        index: targetLineIndex,
        start_grapheme: targetLine.start_grapheme_index,
        end_grapheme: targetLine.end_grapheme_index,
        baseline_y_px: (targetLine.baseline_y_fp / 64).toFixed(2),
        height_px: (targetLine.height_fp / 64).toFixed(2)
      },
      desiredVisualX: this.desiredVisualX
    });

    // 🔧 FIX (2026-01-20): Find the grapheme position on target line closest to desiredVisualX
    // Filter caret entries for the target line
    const targetLineStart = targetLine.start_grapheme_index;
    const targetLineEnd = targetLine.end_grapheme_index;

    // 🐛 FIX (2026-01-20): Handle terminal caret positions at line boundaries
    // Line ranges are half-open [start, end), but the caret can be positioned AT end
    // for terminal cursor positions. We need to include end_grapheme_index in our search
    // UNLESS it's a newline character (we don't want to land on newlines).
    const hasNewline = targetLineEnd > 0 &&
                       targetLineEnd <= text.length &&
                       text[targetLineEnd - 1] === '\n';

    // For non-last lines ending with newline: valid positions are [start, end-1]
    // For last line or lines without newline: valid positions are [start, end]
    // The terminal caret (at end) is valid when it's not a newline
    const maxValidGrapheme = hasNewline ? targetLineEnd - 1 : targetLineEnd;

    // Find closest caret entry by X position within target line
    let bestPos = targetLineStart;
    let bestDist = Infinity;
    let foundAnyCaret = false;

    for (const caret of caretMap) {
      // Only consider carets within the target line range
      // Include terminal position (maxValidGrapheme) for end-of-line cursor
      if (caret.grapheme_index >= targetLineStart && caret.grapheme_index <= maxValidGrapheme) {
        const caretX = caret.x_fp / 64.0;
        const dist = Math.abs(caretX - this.desiredVisualX);
        if (dist < bestDist) {
          bestDist = dist;
          bestPos = caret.grapheme_index;
          foundAnyCaret = true;
        }
      }
    }

    // 🐛 FIX (2026-01-20): If no caret found in range, check for terminal caret at line end
    // This handles the case where cursor is at a line boundary (e.g., position 83 for line [75-83))
    // The terminal caret entry may have grapheme_index == targetLineEnd (exclusive end)
    if (!foundAnyCaret) {
      // Look for caret at exactly targetLineEnd (the exclusive boundary)
      const terminalCaret = caretMap.find(c => c.grapheme_index === targetLineEnd);
      if (terminalCaret && !hasNewline) {
        // Terminal position is valid (not a newline)
        bestPos = targetLineEnd;
        bestDist = Math.abs((terminalCaret.x_fp / 64.0) - this.desiredVisualX);
        foundAnyCaret = true;
      }

      // If still no match, fall back to end of target line
      if (!foundAnyCaret) {
        DEBUG_ENABLED && console.warn('[EdgeDocsInput] ⚠️ No caret entries found for target line, using maxValidGrapheme', {
          targetLineStart,
          targetLineEnd,
          maxValidGrapheme,
          caretMapLength: caretMap.length
        });
        bestPos = maxValidGrapheme;
      }
    }

    // Clamp to valid range
    const newPos = Math.max(targetLineStart, Math.min(maxValidGrapheme, bestPos));

    DEBUG_ENABLED && console.log('[EdgeDocsInput] ✅ Visual line navigation:', {
      direction: delta > 0 ? 'down' : 'up',
      fromLine: currentLineIndex,
      toLine: targetLineIndex,
      fromPos: pos,
      toPos: newPos,
      desiredVisualX: this.desiredVisualX,
      bestDist: bestDist.toFixed(2)
    });

    if (extendSelection) this.extendSelection(newPos);
    else this.setCursorPosition(newPos);
  }

  // ✅ MANDATORY: WASM-only HOME key (move to visual line start)
  private moveCursorToVisualLineStart(extendSelection = false): void {
    const pos = this.currentState.selection.focus;
    
    if (!this.hasWasmData()) {
      // Fallback: move to start of document
      InputLogger.navigation.homeKey(pos, 0, { reason: 'no_wasm_data' });
      if (extendSelection) this.extendSelection(0);
      else this.setCursorPosition(0);
      return;
    }
    
    const lines = this.getWasmLines();
    const docLen = this.currentState.text.length;
    
    // 🛡️ VALIDATION: Check if lines actually cover the document
    const maxLineEnd = lines.length > 0 
      ? Math.max(...lines.map(l => l.end_grapheme_index))
      : 0;
    const linesAreStale = maxLineEnd < docLen - 10;
    
    // 🛡️ If lines are stale and cursor is beyond line coverage, go to document start
    if (linesAreStale && pos > maxLineEnd) {
      InputLogger.navigation.homeKey(pos, 0, { 
        reason: 'stale_lines',
        maxLineEnd,
        docLen,
        linesCount: lines.length
      });
      if (extendSelection) this.extendSelection(0);
      else this.setCursorPosition(0);
      return;
    }
    
    const { line, index: lineIndex } = this.findWasmLineAt(pos, lines);
    const newPos = line.start_grapheme_index;
    
    // 🔬 DIAGNOSTIC: Log HOME key result with full line details
    InputLogger.navigation.homeKey(pos, newPos, {
      lineIndex,
      lineRange: `[${line.start_grapheme_index}-${line.end_grapheme_index})`,
      totalLines: lines.length,
      allLineRanges: lines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', '),
      extendSelection,
      docLen,
      maxLineEnd
    });
    
    if (extendSelection) this.extendSelection(newPos);
    else this.setCursorPosition(newPos);
  }

  // ✅ MANDATORY: WASM-only END key (move to visual line end)
  private moveCursorToVisualLineEnd(extendSelection = false): void {
    const pos = this.currentState.selection.focus;
    
    if (!this.hasWasmData()) {
      // Fallback: move to end of document
      const docEnd = this.currentState.text.length;
      InputLogger.navigation.endKey(pos, docEnd, { reason: 'no_wasm_data' });
      if (extendSelection) this.extendSelection(docEnd);
      else this.setCursorPosition(docEnd);
      return;
    }
    
    const lines = this.getWasmLines();
    const docLen = this.currentState.text.length;
    
    // 🛡️ VALIDATION: Check if lines actually cover the document
    // During rapid typing, cache can be stale/incomplete
    const maxLineEnd = lines.length > 0 
      ? Math.max(...lines.map(l => l.end_grapheme_index))
      : 0;
    const linesAreStale = maxLineEnd < docLen - 10; // Allow small discrepancy for newlines
    
    // 🛡️ If lines are stale, fall back to document end
    if (linesAreStale) {
      InputLogger.navigation.endKey(pos, docLen, {
        reason: 'stale_lines',
        maxLineEnd,
        docLen,
        linesCount: lines.length
      });
      if (extendSelection) this.extendSelection(docLen);
      else this.setCursorPosition(docLen);
      return;
    }
    
    const { line, index: lineIndex } = this.findWasmLineAt(pos, lines);
    
    // Line range is half-open: [start, end) where end is exclusive
    // - For non-last lines: end_grapheme_index points to the NEWLINE character
    // - For last line: end_grapheme_index points one past the last character
    // - END should place cursor AFTER the last VISIBLE character (before newline)
    const isLastLine = lineIndex === lines.length - 1;
    const isEmpty = line.start_grapheme_index === line.end_grapheme_index;
    
    let newPos: number;
    if (isEmpty) {
      // Empty line: cursor goes to start (which equals end)
      newPos = line.start_grapheme_index;
    } else if (isLastLine) {
      // Last line: go to end_grapheme_index (one past last char, no newline)
      newPos = line.end_grapheme_index;
    } else {
      // 🔥 FIX: Non-last lines end with newline at end_grapheme_index
      // END should go to end_grapheme_index - 1 (BEFORE the newline)
      // This places cursor after last visible char like "test|" not "test\n|"
      newPos = line.end_grapheme_index - 1;
    }
    
    // 🔬 DIAGNOSTIC: Log END key result with full line details
    InputLogger.navigation.endKey(pos, newPos, {
      lineIndex,
      lineRange: `[${line.start_grapheme_index}-${line.end_grapheme_index})`,
      isLastLine,
      isEmpty,
      totalLines: lines.length,
      allLineRanges: lines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', '),
      extendSelection,
      docLen,
      maxLineEnd
    });
    
    if (extendSelection) this.extendSelection(newPos);
    else this.setCursorPosition(newPos);
  }

  /**
   * 🔧 FIX (2026-01-20): Check if there's an active selection (anchor !== focus)
   */
  private hasSelection(): boolean {
    return this.currentState.selection.anchor !== this.currentState.selection.focus;
  }

  /**
   * Clear selection
   * 🔧 FIX (2026-01-20): Also clear visual highlight to prevent residue after delete
   */
  private clearSelection(): void {
    const cursorPos = this.currentState.selection.focus;
    this.currentState.selection = { anchor: cursorPos, focus: cursorPos };
    // Clear visual highlight (prevents selection residue on overlay canvas)
    this.updateSelectionHighlights();
  }

  /**
   * Select all text
   */
  private selectAll(): void {
    const selection: SelectionRange = { anchor: 0, focus: this.currentState.text.length };
    this.currentState.selection = selection;
    this.config.onSelectionChange?.(selection);
    this.updateSelectionHighlights(); // Add visual highlighting for CTRL-A
    this.emitCursorChange(selection.focus); // keep caret in sync
  }

  /**
   * Copy selection to clipboard
   */
  private copySelection(): void {
    const { start, end } = normalizeSelection(this.currentState.selection);
    if (start !== end) {
      const selectedText = this.currentState.text.slice(start, end);
      navigator.clipboard.writeText(selectedText).catch((err) => InputLogger.editError('clipboard-copy-failed', err as Error));
    }
  }

  /**
   * Cut selection
   */
  private cutSelection(): void {
    if (this.currentState.selection) {
      this.copySelection();
      this.deleteSelection();
    }
  }

  /**
   * Paste from clipboard
   *
   * Normalizes line endings from clipboard text to MS Word model:
   * - \r\n (Windows) → \r (paragraph break)
   * - \n (Unix/Linux) → \r (paragraph break)
   * - \r (old Mac) → \r (paragraph break)
   *
   * In most external text sources, newlines represent paragraph breaks,
   * so we convert them all to \r for proper paragraph detection in WASM.
   */
  private async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        // Normalize line endings to \r (paragraph break) for MS Word model
        // Windows \r\n → \r, Unix \n → \r, old Mac \r stays \r
        const normalizedText = text.replace(/\r\n/g, '\r').replace(/\n/g, '\r');
        this.insertText(normalizedText);
      }
    } catch (error) {
      InputLogger.editError('clipboard-paste-failed', error as Error);
    }
  }

  /**
   * Undo - wire to real history system
   * ✅ OPTION C: Use SharedInputContext's UndoManager for WASM-based undo
   */
  private undo(): void {
    // Option C: Use external UndoManager (WASM-based)
    if (this.config.useOptionC && this.config.onUndo) {
      this.config.onUndo();
      return;
    }
    
    // Fallback: Use local history (non-WASM)
    const result = this.handleUndo();
    this.updateState(result.state);
  }

  /**
   * Redo - wire to real history system
   * ✅ OPTION C: Use SharedInputContext's UndoManager for WASM-based redo
   */
  private redo(): void {
    // Option C: Use external UndoManager (WASM-based)
    if (this.config.useOptionC && this.config.onRedo) {
      this.config.onRedo();
      return;
    }
    
    // Fallback: Use local history (non-WASM)
    const result = this.handleRedo();
    this.updateState(result.state);
  }

  /**
   * Update text and notify
   */
  private updateText(newText: string, cursorPosition: number): void {
    // 🚨 CRITICAL DIAGNOSTIC: Track cursor position through update flow
    DEBUG_ENABLED && console.log('🔍 [updateText] CALLED:', {
      newTextLength: newText.length,
      cursorPosition,
      oldTextLength: this.currentState.text.length,
      oldCursorPosition: this.currentState.selection.focus,
      oldSelection: { anchor: this.currentState.selection.anchor, focus: this.currentState.selection.focus }
    });
    
    this.currentState.text = newText;
    this.currentState.cursorPosition = cursorPosition;
    this.currentState.selection = { anchor: cursorPosition, focus: cursorPosition };
    
    debugLog('🔍 [updateText] State updated:', {
      textLength: this.currentState.text.length,
      cursorPosition
    });
    
    // ✅ FIX: Track last sent text to prevent echo-back overwrite
    (this as any).lastSentText = newText;
    
    // Notify React of text change
    this.config.onTextChange(this.currentState.text, this.currentState.selection);
    
    // Emit cursor change immediately for caret positioning
    this.emitCursorChange(cursorPosition);
  }


  /**
   * Record input event
   */
  private recordEvent(event: InputEvent): void {
    this.eventHistory.push(event);
    // Keep only last 100 events
    if (this.eventHistory.length > 100) {
      this.eventHistory.shift();
    }
  }

  /**
   * Get current state
   */
  getCurrentState(): DocumentState {
    return { ...this.currentState };
  }

  /**
   * Paste text at current cursor position (public API)
   *
   * This method properly handles Option C by sending the paste as an edit command
   * to WASM, ensuring the text is properly shaped and rendered.
   *
   * Normalizes line endings to MS Word model:
   * - \r\n (Windows) → \r (paragraph break)
   * - \n (Unix/Linux) → \r (paragraph break)
   * - \r (old Mac) → \r (paragraph break)
   *
   * @param text - The text to paste (will be normalized for line endings)
   */
  pasteText(text: string): void {
    // Normalize line endings to \r (paragraph break) for MS Word model
    const normalizedText = text.replace(/\r\n/g, '\r').replace(/\n/g, '\r');

    DEBUG_ENABLED && console.log('📋 [EdgeDocsInput] pasteText called:', {
      textLength: normalizedText.length,
      textPreview: normalizedText.slice(0, 50),
      useOptionC: this.config.useOptionC
    });

    // Use the internal insertText method which handles Option C properly
    this.insertText(normalizedText);
  }

  /**
   * Set new state (called by React useEffect to sync state back to handler)
   */
  setState(state: DocumentState): void {
    debugLog('🔍 [setState] Syncing state:', {
      oldCursorPos: this.currentState.selection.focus,
      newCursorPos: state.selection.focus,
      textLengthChange: state.text.length - this.currentState.text.length
    });
    
    this.currentState = { ...state };
  }


  /**
   * Get input event history (for debugging)
   */
  getEventHistory(): InputEvent[] {
    return [...this.eventHistory];
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
  }
}

/**
 * React hook for input handling
 * 
 * @param initialText - Initial text content
 * @param onTextChange - Callback when text changes (for optimistic mode)
 * @param maxLength - Maximum text length
 * @param optionCConfig - Optional: Enable Option C (WASM as single source of truth)
 */
export const useEdgeDocsInput = (
  initialText: string,
  onTextChange: (text: string, selection: SelectionRange) => void,
  maxLength?: number,
  optionCConfig?: {
    enabled: boolean;
    onEditCommand: (command: EditCommand) => Promise<EditCommandResult>;
    onUndo?: () => Promise<void>;
    onRedo?: () => Promise<void>;
  }
) => {
  const [inputHandler] = React.useState(() => {
    const config: InputHandlerConfig = {
      onTextChange,
      maxLength,
      // ✅ MANDATORY: WASM data providers (initially empty, set via setLayoutProviders)
      getWasmLines: () => [],
      getCaretMap: () => [],
      getGraphemeToUtf16: () => [],
      // ✅ OPTION C: WASM as single source of truth
      useOptionC: optionCConfig?.enabled ?? false,
      onEditCommand: optionCConfig?.onEditCommand,
      onUndo: optionCConfig?.onUndo,
      onRedo: optionCConfig?.onRedo
    };
    const initialState: DocumentState = {
      text: initialText,
      selection: { anchor: 0, focus: 0 },  // ✅ Start at beginning of document
      cursorPosition: 0  // ✅ Start at grapheme index 0 (document start)
    };
    return new EdgeDocsInputHandler(config, initialState);
  });

  // CRITICAL: Keep input handler state synchronized with React state
  // ⚠️ CRITICAL: Only sync when parent text is genuinely different
  // This prevents race conditions where local edits get overwritten by stale parent updates
  React.useEffect(() => {
    const currentHandlerState = inputHandler.getCurrentState();
    
    // ✅ CRITICAL FIX: Don't sync if handler text is AHEAD of initialText (we typed)
    // This prevents cursor from resetting when we type
    // Only sync when initialText is AHEAD of handler text (external update)
    const handlerTextLength = currentHandlerState.text.length;
    const initialTextLength = initialText.length;
    const isHandlerAhead = handlerTextLength > initialTextLength;
    const isInitialAhead = initialTextLength > handlerTextLength;
    const textsEqual = currentHandlerState.text === initialText;
    
    // ✅ CRITICAL: Skip sync if texts are equal (no change needed)
    if (textsEqual) {
      return; // No sync needed - texts match
    }
    
    // ✅ CRITICAL: Skip sync if handler is ahead (we just typed)
    // This prevents our local edits from being overwritten
    if (isHandlerAhead) {
      debugLog('🔄 [useEdgeDocsInput] Skipping sync - handler is ahead (we typed)');
      return;
    }
    
    // ✅ FIX: Don't overwrite if parent is just echoing back our last update
    const lastSentText = (inputHandler as any).lastSentText;
    if (lastSentText === initialText) {
      debugLog('🔄 [useEdgeDocsInput] Skipping sync - parent echoing back our update');
      return;
    }
    
    // ✅ CRITICAL FIX: Preserve cursor position when syncing text
    const currentCursorPos = currentHandlerState.selection.focus;
    const newCursorPos = Math.min(currentCursorPos, initialText.length);
    
    debugLog('🔄 [useEdgeDocsInput] Syncing text from parent (external update)');
    
    inputHandler.setState({
      text: initialText,
      selection: { anchor: newCursorPos, focus: newCursorPos },
      cursorPosition: newCursorPos
    });
    
    // 🚨 CRITICAL DIAGNOSTIC: Verify state after sync
    DEBUG_ENABLED && console.log('🔄 [useEdgeDocsInput] After sync - verifying state:', {
      expectedCursorPos: newCursorPos,
      actualCursorPos: inputHandler.getCurrentState().selection.focus,
      matches: inputHandler.getCurrentState().selection.focus === newCursorPos
    });
  }, [initialText, inputHandler]);

  const handleKeyDown = React.useCallback((event: KeyboardEvent) => {
    return inputHandler.handleKeyDown(event);
  }, [inputHandler]);
  
  // ✅ V9: IME composition handlers
  const handleCompositionStart = React.useCallback((event: CompositionEvent) => {
    inputHandler.handleCompositionStart(event);
  }, [inputHandler]);
  
  const handleCompositionUpdate = React.useCallback((event: CompositionEvent) => {
    inputHandler.handleCompositionUpdate(event);
  }, [inputHandler]);
  
  const handleCompositionEnd = React.useCallback((event: CompositionEvent) => {
    inputHandler.handleCompositionEnd(event);
  }, [inputHandler]);

  const getCurrentState = React.useCallback(() => {
    return inputHandler.getCurrentState();
  }, [inputHandler]);

  const setState = React.useCallback((state: DocumentState) => {
    inputHandler.setState(state);
  }, [inputHandler]);

  const setCursorPosition = React.useCallback((pos: number) => {
    DEBUG_ENABLED && console.log('🎯 [useEdgeDocsInput] setCursorPosition (public) called:', { requested: pos });
    const s = inputHandler.getCurrentState();
    DEBUG_ENABLED && console.log('🎯 [useEdgeDocsInput] Current state:', { textLength: s.text.length, currentFocus: s.selection.focus });
    const newPos = Math.max(0, Math.min(pos, s.text.length));
    const selection = { anchor: newPos, focus: newPos };
    inputHandler.setState({ ...s, selection, cursorPosition: newPos });
    DEBUG_ENABLED && console.log('🎯 [useEdgeDocsInput] After setState, new position:', newPos);
    // IMPORTANT: cursor-only notify (overlay moves), do NOT call onTextChange here
    (inputHandler as any).emitCursorChange?.(newPos);
  }, [inputHandler]);

  // ✅ MANDATORY: DI setters for WASM-only data providers
  const setLayoutProviders = React.useCallback((providers: {
    getWasmLines: () => Line[];
    getCaretMap: () => CaretPosition[];
    getGraphemeToUtf16: () => number[];
    onSelectionHighlightChange?: (ranges: Array<{start: number, end: number}>) => void;
    // 🆕 FALLBACK: Get ALL lines from typesetter client cache (for cross-page navigation)
    getAllCachedLines?: () => Line[];
    getAllCachedCaretPositions?: () => CaretPosition[];
  }) => {
    Object.assign((inputHandler as any)['config'], providers);
  }, [inputHandler]);

  const setOnCursorChange = React.useCallback((cb: (idx: number, caret: {x:number;y:number;height:number}) => void) => {
    (inputHandler as any)['config'].onCursorChange = cb;
  }, [inputHandler]);

  const dispatch = React.useCallback(async (cmd: InputCommand) => {
    return await inputHandler.dispatch(cmd);
  }, [inputHandler]);

  const beginSelectionDrag = React.useCallback((anchor: number) => {
    inputHandler.beginSelectionDrag(anchor);
  }, [inputHandler]);

  const updateSelectionDrag = React.useCallback((current: number) => {
    inputHandler.updateSelectionDrag(current);
  }, [inputHandler]);

  const endSelectionDrag = React.useCallback(() => {
    inputHandler.endSelectionDrag();
  }, [inputHandler]);

  const setSelection = React.useCallback((selection: SelectionRange) => {
    inputHandler.setSelection(selection);
  }, [inputHandler]);

  const pasteText = React.useCallback((text: string) => {
    inputHandler.pasteText(text);
  }, [inputHandler]);

  // 🆕 Expose setUndoRedoCallbacks for wiring after initialization
  const setUndoRedoCallbacks = React.useCallback((onUndo?: () => Promise<void>, onRedo?: () => Promise<void>) => {
    inputHandler.setUndoRedoCallbacks(onUndo, onRedo);
  }, [inputHandler]);

  // 🆕 2026-01-21: Expose setFormattingCallbacks for Ctrl+B/Ctrl+I keyboard shortcuts
  const setFormattingCallbacks = React.useCallback((onToggleBold?: () => Promise<void>, onToggleItalic?: () => Promise<void>) => {
    inputHandler.setFormattingCallbacks(onToggleBold, onToggleItalic);
  }, [inputHandler]);

  // 🔧 FIX (2026-01-24): Expose setOnSelectionChange for toolbar formatting operations
  const setOnSelectionChange = React.useCallback((callback?: (selection: SelectionRange) => void) => {
    inputHandler.setOnSelectionChange(callback);
  }, [inputHandler]);

  // 🔥 PERFORMANCE FIX: Memoize the return object to prevent new object reference on every render
  // Without this, consumers using this object as a useEffect dependency will re-run on every render,
  // causing keyboard listener setup/teardown storms.
  return React.useMemo(() => ({
    handleKeyDown,
    handleCompositionStart,  // ✅ V9: IME composition
    handleCompositionUpdate, // ✅ V9: IME composition
    handleCompositionEnd,    // ✅ V9: IME composition
    getCurrentState,
    setState,
    setCursorPosition,
    setLayoutProviders,
    setOnCursorChange,
    dispatch,              // NEW: Command dispatch
    beginSelectionDrag,    // NEW: Mouse selection start
    updateSelectionDrag,   // NEW: Mouse selection update
    endSelectionDrag,      // NEW: Mouse selection end
    setSelection,          // NEW: Direct selection set
    pasteText,             // NEW: Paste text (Option C compatible)
    setUndoRedoCallbacks,  // 🆕 Wire undo/redo callbacks after init
    setFormattingCallbacks, // 🆕 2026-01-21: Wire formatting callbacks for Ctrl+B/Ctrl+I
    setOnSelectionChange,   // 🔧 FIX (2026-01-24): Selection change callback for toolbar
    eventHistory: inputHandler.getEventHistory()
  }), [
    handleKeyDown,
    handleCompositionStart,
    handleCompositionUpdate,
    handleCompositionEnd,
    getCurrentState,
    setState,
    setCursorPosition,
    setLayoutProviders,
    setOnCursorChange,
    dispatch,
    beginSelectionDrag,
    updateSelectionDrag,
    endSelectionDrag,
    setSelection,
    pasteText,
    setUndoRedoCallbacks,
    setFormattingCallbacks,
    setOnSelectionChange,
    inputHandler
  ]);
};
